import {
  Activity,
  AlertTriangle,
  BarChart3,
  Blocks,
  CalendarClock,
  CheckCircle2,
  ChevronDown,
  CircleSlash2,
  Database,
  Gauge,
  GitBranch,
  KeyRound,
  ExternalLink,
  Landmark,
  LayoutGrid,
  LoaderCircle,
  LockKeyhole,
  Network,
  PauseCircle,
  RefreshCw,
  ScrollText,
  Search,
  ShieldCheck,
  Trophy,
  Users,
} from 'lucide-react';
import { type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MatrixGraph } from '../components/MatrixGraph';
import { GenerationIncomeMap } from '../components/GenerationIncomeMap';
import { PrimaryTokenCard } from '../components/PrimaryTokenCard';
import { OnChainMatrixSection } from '../components/OnChainMatrixCard';
import { AdminEngineSection } from '../components/AdminEngineSection';
import { PositionLegend } from '../components/PositionGrid';
import { ALL_SLOT_KEYS, POSITION_NUMBERS, ProjectedSlotPanel, slotGridSource } from '../components/SlotMatrix';
import { SUB_SLOTS, compareSlots, nextSlot, slotLabel, slotTitle } from '../lib/slots';
import { Dropdown, PageHeading, StatCard, StatusPill } from '../components/Ui';
import type { MatrixNode } from '../data/view-model';
import { ApiError, api } from '../lib/api';
import { formatDate, formatToken, shortenAddress } from '../lib/format';
import { AdminTokenUnfreezeReport } from '../components/AdminTokenUnfreezeReport';
import { levelAmountsText } from '../components/IdReports';
import { AdminSlotTestTool } from '../components/AdminSlotTestTool';
import { AdminEraTestTool } from '../components/AdminEraTestTool';
import { AdminCdtBenefitSection, CdtSettledLine } from '../components/AdminCdtBenefitSection';
import { CdtBenefitCard, useCdtBenefitParams } from '../components/CdtBenefitCard';
import { normalizeCode } from '../lib/cdt-benefit';
import { compareCountries, countryLabel, countryMatches } from '../lib/countryNames';
import { Pager } from '../components/Pager';
import { TokenLiquiditySummary, btcValue, burnProgress, ratioToBps, useTokenLiquidity } from '../components/TokenLiquidityInfo';
import type { CycleDetail, EngineParameters, LookupResult, PrimaryTokenView, ProjectedMatrixCycle, ProjectedSlotView } from '../lib/lookup-types';
import { atomicToDecimal, useAppData } from '../state/AppDataContext';
import { useWallet } from '../state/WalletContext';
import { LEADERS_CLUB_ABI, MATRIX_ABI, resolveGovernanceAddresses, sendGovernanceCall } from '../lib/governance';
import { EXPLORER_URL } from '../lib/network';

/** Payment-flow rows whose money does not stay: it moves on and fills another seat (counted again there). */
const FLOW_PASS_THROUGH = new Set(['sponsor_one', 'sponsor_two', 'downline_route', 'recycle', 'upgrade_released']);

/**
 * Owner report 2026-11-01: "Register test ID" showed "Failed to fetch" while the API finished its 4-5 transactions and
 * created the ID anyway (the browser's POST dropped at network level, the server kept working). After such a failure the
 * page re-reads the registers at these delays and reports the row it finds. Exported (mutable) so tests can shorten it.
 */
export const TEST_REGISTER_RECHECK_DELAYS_MS: number[] = [5_000, 20_000];
/** Client-side cap for the test-register POST; the browser must not give up before the server (nginx proxy_read_timeout 180 s). */
const TEST_REGISTER_TIMEOUT_MS = 180_000;
/** Server rows created this long before the click still count as "ours" (server and browser clocks are not in sync). */
const TEST_REGISTER_CLOCK_SKEW_MS = 60_000;

/** Same single-leg node reconstruction used on the public lookup page, from raw admin cycle data. */
function buildAdminMatrixNodes(cycle: CycleDetail): MatrixNode[] {
  const positions = Math.max(12, cycle.totalPositions);
  return Array.from({ length: positions }, (_, index) => {
    const position = index + 1;
    const placement = cycle.placements.find((item) => item.positionNumber === position);
    if (!placement) return { position, kind: 'empty', state: 'empty' };
    return {
      position,
      memberId: placement.memberBdId,
      kind: placement.placementType === 'virtual' ? 'treasury' : 'downline',
      state: 'confirmed',
    };
  });
}

/** Maps the redirect-pattern engine's richer event kinds down to the matrix graph's visual kinds. */
function adminProjectedKindToNodeKind(kind: string): MatrixNode['kind'] {
  switch (kind) {
    case 'sponsor_one':
    case 'sponsor_two':
    case 'downline_route':
      return 'downline';
    case 'slot_owner':
      return 'income';
    case 'upgrade_hold':
    case 'upgrade_complete':
    case 'token_liquidity':
    case 'leaders_club':
    case 'treasury_fallback':
      return 'treasury';
    case 'recycle':
      return 'recycle';
    default:
      return 'empty';
  }
}

/** Builds matrix nodes from an on-chain slot view cycle (the API's indexed SeatFilled seats). */
function buildAdminProjectedMatrixNodes(cycle: ProjectedMatrixCycle): MatrixNode[] {
  return Array.from({ length: 12 }, (_, index) => {
    const position = index + 1;
    const event = cycle.positions.find((item) => item.position === position);
    if (!event || event.kind === 'open') {
      return { position, kind: 'empty', state: 'empty' };
    }
    return event.occupantId
      ? { position, memberId: event.occupantId, kind: adminProjectedKindToNodeKind(event.kind), state: 'confirmed' }
      : { position, kind: adminProjectedKindToNodeKind(event.kind), state: 'confirmed' };
  });
}

interface AdminOverview {
  authorized: boolean;
  /** apps/api getAdminOverview(): every count / amount read from the indexed contract events. */
  stats: {
    source?: 'onchain';
    totalMembers: number;
    placedMembers?: number;
    activeMembers: number;
    completedCycles: number;
    confirmedTransactions: number;
    distributedAtomic: string;
    seatsFilled?: number;
    routingEvents?: number;
    routingTreasuryFallbacks?: number;
    sponsorIncomePaidAtomic?: string;
    sponsorIncomeMissedAtomic?: string;
    claimedAtomic?: string;
    lastEventAt?: string | null;
  };
  rpc: Array<{
    id: string;
    priority: number;
    healthy: boolean;
    latencyMs: number | null;
    consecutiveFailures: number;
    lastCheckedAt: string | null;
  }>;
}

interface AuditRecord {
  id: number;
  actorWallet: string | null;
  action: string;
  resource: string;
  resourceId: string | null;
  outcome: string;
  createdAt: string;
}

interface AuditPage {
  items: AuditRecord[];
  cursor: number;
  limit: number;
}

type AdminState = 'loading' | 'authorized' | 'denied';
type AdminAction = 'probe' | 'run' | 'reconcile' | 'rebuild';

interface RegistrationRow {
  bdId: string;
  walletAddress: string;
  sponsorBdId: string | null;
  displayName: string;
  rank: string;
  status: 'pending' | 'active' | 'suspended';
  joinedAt: string;
  /** On-chain: IdCreated era, JoinPlaced, highest real slot, placed directs, join transaction. */
  era?: string;
  placed?: boolean | null;
  placedAt?: string | null;
  highestSlot?: number;
  highestSlotLabel?: string;
  directCount?: number;
  joinTxHash?: string | null;
  /** docs/CDT_BENEFIT_SPEC.md s.2 - countryOf(id) from the CDT Benefit contract (CountrySelected); absent until the API has it. */
  country?: string | null;
}

interface RegistrationsPage {
  items: RegistrationRow[];
  totalCount: number;
}

/** One level of SponsorIncome events: 1 = direct sponsor income, 2..10 = level income. */
interface IncomeLevelTotal {
  level: number;
  count: number;
  amountAtomic: string;
}

/** apps/api listIncomeReport(): the SponsorIncome events that paid each ID (on-chain, owner 2026-10-11). */
interface IncomeReportRow {
  bdId: string;
  joinedAt: string;
  directPartners: number;
  levelTwoToSixPartners: number;
  receivedTotalAtomic: string;
  directIds: string[];
  directIdsTruncated: boolean;
  levelIds: Array<{ bdId: string; depth: number }>;
  levelIdsTruncated: boolean;
  directTotalAtomic: string;
  levelTotalAtomic: string;
  secondGenCount: number;
  secondGenTotalAtomic: string;
  deepGenCount: number;
  deepGenTotalAtomic: string;
  byLevel: IncomeLevelTotal[];
  netReceivedAtomic: string;
  feeAtomic: string;
  lastPaidAt: string | null;
}

interface IncomeReportTotals {
  paidCount: number;
  paidAtomic: string;
  missedCount: number;
  missedAtomic: string;
  emittedCount: number;
  emittedAtomic: string;
  recipientCount: number;
  netAtomic: string;
  feeAtomic: string;
}

interface IncomeReportPage {
  items: IncomeReportRow[];
  totalCount: number;
  /** Sponsor-level amounts as configured on-chain (EconomicsConfigured); empty until indexed. */
  ratesByDepthAtomic: string[];
  levelMaxDepth: number;
  totals: IncomeReportTotals;
}

interface IncomeContributor {
  bdId: string;
  joinedAt: string;
  depth: number;
  amountAtomic: string;
  netAtomic: string | null;
  feeAtomic: string | null;
  txHash: string;
  paidAt: string;
}

interface IncomeBreakdown {
  bdId: string;
  receivedTotalAtomic: string;
  netReceivedAtomic: string;
  feeAtomic: string;
  byLevel: IncomeLevelTotal[];
  contributors: IncomeContributor[];
}

/** One SponsorIncome event with recipient 0 (credited to the REFERRAL_TREASURY bucket). */
interface MissedReferralRow {
  sourceBdId: string;
  sourceJoinedAt: string;
  level: number;
  amountAtomic: string;
  reason: 'no-upline' | 'inactive-recipient' | 'chain-stopped';
  intendedRecipientBdId: string | null;
  stoppedAtBdId?: string | null;
  txHash: string;
  at: string;
}

interface MissedReferralReport {
  ratesByDepthAtomic?: string[];
  levelMaxDepth?: number;
  missedDirectAtomic: string;
  missedDirectCount: number;
  missedLevelAtomic: string;
  missedLevelCount: number;
  missedTotalAtomic: string;
  sourceCount: number;
  byLevel: IncomeLevelTotal[];
  referralTreasury: { creditedAtomic: string; creditCount: number; differenceAtomic: string; matches: boolean };
  items: MissedReferralRow[];
}

type MissedFilter = 'direct' | 'level' | 'all';

interface TreasuryLedgerRow {
  eventName: 'InboundRecorded' | 'BucketAllocated' | 'ExternalSettlement' | 'MemberWithdrawal' | 'LiquidationPaid' | 'OwnerWithdrawal';
  amountAtomic: string;
  fromBucket?: string | null;
  deltaAtomic: string;
  runningBalanceAtomic: string;
  bucket: string | null;
  refId: string | null;
  memberBdId: string | null;
  txHash: string;
  blockNumber: number;
  blockTimestamp: string | null;
}

interface TreasuryReport {
  vaultAddress: string | null;
  paymentTokenAddress: string | null;
  onChain: { balanceAtomic: string; totalAccountedAtomic: string; ownerAddress: string; blockNumber: string; readAt: string } | null;
  onChainError: string | null;
  indexed: { inboundAtomic: string; inboundCount: number; settledAtomic: string; settledCount: number; allocationCount: number; withdrawnAtomic?: string; withdrawnCount?: number; netAtomic: string; lastEventAt: string | null };
  ledger: TreasuryLedgerRow[];
  /** Every SeatFilled counted once under its final destination, with the real seat amount. */
  paymentFlow: { totalSeats: number; byDestination: Array<{ kind: string; label: string; count: number; amountAtomic: string; passThrough?: boolean; sharePercent: number }>; finalAtomic: string; passedOnAtomic: string };
  buckets: Array<{ bucket: string; count: number; amountAtomic: string }>;
  /** Pool / treasury accounts from the vault + matrix events (BucketCredited, UpgradeHeld, UpgradeReleased, Escrow*, SponsorIncome). */
  accounts: TreasuryAccount[];
}

interface TreasuryAccountEntry {
  ownerBdId: string | null;
  occupantBdId: string;
  slotNumber: number | null;
  slotLabel: string | null;
  cycleNumber: number | null;
  position: number | null;
  rule: string;
  direction: 'in' | 'out';
  amountAtomic: string;
  txHash: string;
  at: string | null;
  detail?: string;
}

interface TreasuryAccount {
  key: string;
  label: string;
  description: string;
  source: 'onchain';
  bucket: string | null;
  count: number;
  amountAtomic: string;
  inAtomic: string;
  outAtomic: string;
  bucketCreditedAtomic: string | null;
  entries: TreasuryAccountEntry[];
}

/** apps/api listRoutingEvents(): the contract's Routed + RoutingCandidate events. */
interface RoutingEventRow {
  routingEventId: number;
  routingType: string;
  sourceId: string;
  slotOwnerId: string;
  sourceSlotNumber: number;
  sourceCycleNumber: number;
  sourcePosition: number;
  routingDepth: number;
  candidateEvaluationCount: number;
  candidatePath: string[];
  candidates?: Array<{ index: number; bdId: string; eligible: boolean }>;
  selectedCandidateId: string | null;
  destinationSlotNumber: number;
  destinationCycleNumber: number | null;
  destinationPosition: number | null;
  finalDestinationType: 'placement' | 'treasury';
  txHash?: string;
  at?: string;
}

interface RoutingEventsPage {
  items: RoutingEventRow[];
  totalCount: number;
  /** On-chain senior-sponsor check limit; null until the contract's limit event is indexed. */
  uplineSearchLimit?: number | null;
}

const ROUTING_TYPE_LABEL: Record<string, string> = {
  sponsor_one: 'Sponsor-1 (P1)',
  sponsor_two: 'Sponsor-2 (C1 P6 / C2+ P3)',
  downline_first: 'First direct downline (C1 P8)',
  downline_second: 'Second direct downline (C2+ P5)',
  upgrade: 'Upgrade to next slot (P3 + P4)',
  recycle: 'Recycle payment routing (P12, all cycles)',
};

/** Human label for an on-chain treasury-account rule (seat kind, routing type or vault event). */
function ruleLabel(rule: string): string {
  if (rule.startsWith('level_')) return `Sponsor level ${rule.slice(6)} - no placed upline`;
  switch (rule) {
    case 'sponsor_one': return 'Sponsor-1 routing (up) - nobody eligible';
    case 'sponsor_two': return 'Sponsor-2 routing (up) - nobody eligible';
    case 'downline_first':
    case 'downline_second': return 'Downline routing - nobody eligible';
    case 'recycle': return 'Recycle routing (P12) - nobody eligible';
    case 'routing': return 'Routing - nobody eligible';
    case 'slot_entry': return 'Upgrade entry - no eligible host';
    case 'join_fallback': return 'Join - sponsor has no matrix in the entry slot';
    case 'token_liquidity': return 'Token liquidity seat';
    case 'leaders_club': return 'Leaders Club seat';
    case 'upgrade_hold': return 'Upgrade holding (C1 P3)';
    case 'upgrade_complete': return 'Upgrade holding (C1 P4)';
    case 'upgrade_refill': return 'Refill after a forfeiture (rule 34)';
    case 'upgrade_released': return 'Released into the next-slot entry';
    case 'escrow_parked': return 'Dormant holding parked (rule 21)';
    case 'escrow_reclaimed': return 'Parked holding reclaimed';
    case 'escrow_forfeited': return 'Parked holding forfeited';
    default: return rule;
  }
}

type TreasuryView = string;

/*
 * Treasury pool tiles (client 2026-10-09; on-chain only since 2026-10-11): each tile shows ONLY the vault bucket balance
 * read live from the contract. A click opens that bucket's on-chain account - every credit / movement the contracts
 * emitted, paired with the seat that produced it - which explains how the balance accumulated.
 */
const TREASURY_POOL_TILES: Array<{ bucket: string; label: string; accountKey: string | null }> = [
  { bucket: 'ROUTING_TREASURY', label: 'Routing treasury', accountKey: 'routing_fallback' },
  { bucket: 'SLOT_FALLBACK_TREASURY', label: 'Slot-fallback treasury', accountKey: 'slot_entry' },
  { bucket: 'REFERRAL_TREASURY', label: 'Referral treasury', accountKey: 'missed_referral' },
  { bucket: 'TOKEN_LIQUIDITY', label: 'Token liquidity pool', accountKey: 'token_liquidity' },
  { bucket: 'LEADERS_CLUB', label: 'Leaders Club pool', accountKey: 'leaders_club' },
  { bucket: 'UPGRADE_ESCROW', label: 'Upgrade escrow (holding)', accountKey: 'upgrade_hold' },
  { bucket: 'TREASURY_RESERVE', label: 'Treasury reserve (parked holdings)', accountKey: 'treasury_reserve' },
];
const TREASURY_TILE_ACCOUNT_KEYS = new Set(TREASURY_POOL_TILES.map((tile) => tile.accountKey).filter((key): key is string => key !== null));

/** apps/api getSlotDistribution(): on-chain slot activations (indexed_matrix_slots), ¼, ½, 1 .. 16. */
interface SlotDistributionRow {
  slotNumber: number;
  slotLabel?: string;
  activeCount: number;
  virtualCount: number;
  totalCount: number;
  upgradedCount: number;
  upgradeWaitingCount: number;
  seatsFilled: number;
  cyclesCompleted: number;
}

interface LeadersClubMember {
  bdId: string;
  highestSlot: number;
  joinedAt: string;
  enteredAt: string | null;
  exitedAt: string | null;
  exitedToClub: number | null;
  directCount: number;
  eligible: boolean;
}

interface LeadersClubPastMember extends LeadersClubMember {
  club: number;
}

interface LeadersClubSettlement {
  id: number;
  settledAt: string;
  periodStart: string;
  periodEnd: string;
  fundAtomic: string;
  memberCount: number;
  undistributedAtomic: string;
  note: string | null;
  payouts: Array<{ club: number; bdId: string; amountAtomic: string }>;
}

interface LeadersClubPool {
  club: number;
  label: string;
  sharePercent: number;
  poolAtomic: string;
  memberCount: number;
  perMemberAtomic: string;
  undistributedAtomic: string;
  members: LeadersClubMember[];
  lifetime: boolean;
  shareAtomic: string;
  carriedInAtomic: string;
  projectedOnlyCount: number;
}

interface TestRegistration { id: number; bdId: string; wallet: string; sponsorBdId: string; era: number; entryAmountAtomic: string; btcbSource: string; joinTx: string; createdAt: string; /** docs/CDT_BENEFIT_SPEC.md s.2 - country sent to selectCountry after the join. */ country?: string | null }
interface TestRegisterResult {
  bdId: string; wallet: string; privateKey: string; sponsorBdId: string; era: number; entryAmountAtomic: string; gasWei: string; btcbSource: string;
  txs: { gas: string; btcb: string; approve: string; join: string; selectCountry?: string | null };
  country?: string | null;
  countrySelection?: { requested: string | null; tx: string | null; error: string | null } | null;
  funder: { address: string; bnbBeforeWei: string; bnbAfterWei: string; btcbBeforeAtomic: string; btcbAfterAtomic: string };
}

/** rules/rule14.txt: admin register of every member's Primary Token (ID + T). */
interface PrimaryTokenRegister {
  items: PrimaryTokenView[];
  memberCount: number;
  mintedCount: number;
  identityMismatchCount: number;
  duplicateIds: string[];
}

interface LeadersClubReport {
  fundAtomic: string;
  seatCount: number;
  periodWeeks: number;
  pools: LeadersClubPool[];
  totalMembers: number;
  undistributedAtomic: string;
  settledAtomic: string;
  pendingAtomic: string;
  settlements: LeadersClubSettlement[];
  pastMembers: LeadersClubPastMember[];
  periodDays: number;
  defaultPeriodDays: number;
  nextSettlementAt: string | null;
  settlementDue: boolean;
  /** On-chain Leaders Club (indexed BDynamicLeadersClub events) - the primary figures. */
  onChain?: LeadersClubOnChain;
}

/** apps/api getLeadersClubReport().onChain - read from the indexed contract events. */
interface LeadersClubOnChain {
  available: boolean;
  creditedAtomic: string;
  creditedCount: number;
  paidAtomic: string;
  payoutCount: number;
  totalMembers: number;
  pools: Array<{ club: number; label: string; lifetime: boolean; memberCount: number; eligibleCount: number; paidAtomic: string; carriedOutAtomic: string; members: Array<{ bdId: string; enteredAt: string; directCount: number; eligible: boolean; receivedAtomic: string }> }>;
  settlements: Array<{ settlementId: number; fundAtomic: string; settledAt: string; nextSettlementAt: string; txHash: string; pools: Array<{ club: number; shareAtomic: string; carriedInAtomic: string; eligibleCount: number; paidAtomic: string; carriedOutAtomic: string }>; payouts: Array<{ club: number; bdId: string; amountAtomic: string; txHash: string; at: string }> }>;
  settlementCount: number;
  lastSettledAt: string | null;
  nextSettlementAt: string | null;
  periodMode: string | null;
  periodSeconds: string | null;
}

/** ID browser slot: always a slot the contract activated (indexed_matrix_slots), seats from SeatFilled. */
interface BrowseSlotView extends ProjectedSlotView {
  onChain: boolean;
  slotStatus: 'active';
  activatedAt: string | null;
}

interface BrowseMemberRow {
  bdId: string;
  sponsorBdId: string | null;
  joinedAt: string;
  /** Placed directs (the contract's qualification count). */
  directCount: number;
  /** Every slot this ID holds on-chain, progression order. */
  activatedSlots: number[];
  /** Virtual sub-slots (Slot ½ / ¼ that only seat lower-era IDs); absent from older APIs. */
  virtualSlots?: number[];
  slots: BrowseSlotView[];
}

interface BrowseMembersPage {
  items: BrowseMemberRow[];
  totalCount: number;
  availableCycles: number[];
  positionsSelected: number[] | null;
}

/**
 * Single / multi / range selector (rules/rule7.txt addendum C): click picks one, "Multi" adds each
 * click, and the range box accepts "2-5" or "1,3,7-9" style text on top of the chips.
 */
function SelectionPicker({
  label,
  prefix,
  options,
  selected,
  onChange,
  range,
  onRange,
  hint,
  format = String,
  compare = (a: number, b: number) => a - b,
}: {
  label: string;
  prefix: string;
  options: readonly number[];
  selected: number[];
  onChange(next: number[]): void;
  range: string;
  onRange(next: string): void;
  hint: string;
  /** Chip text after the prefix (slots: '½' / '¼' for the sub-slot keys 50 / 25). */
  format?: (value: number) => string;
  /** Order of the selected values (slots: progression rank, never numeric). */
  compare?: (a: number, b: number) => number;
}) {
  const [multi, setMulti] = useState(false);
  const toggle = (value: number) => {
    if (!multi) {
      onChange(selected.length === 1 && selected[0] === value ? [] : [value]);
      return;
    }
    onChange(selected.includes(value) ? selected.filter((item) => item !== value) : [...selected, value].sort(compare));
  };
  return (
    <div className="selection-picker">
      <div className="selection-picker-head">
        <span className="browse-label">{label}</span>
        <button type="button" className={`mode-toggle${multi ? ' on' : ''}`} onClick={() => setMulti((value) => !value)} title="Single: one click picks one. Multi: every click adds or removes one.">{multi ? 'Multi' : 'Single'}</button>
        <button type="button" className="mode-toggle" onClick={() => { onChange([]); onRange(''); }} disabled={selected.length === 0 && !range}>All</button>
      </div>
      <div className="selection-chips">
        {options.map((value) => (
          <button key={value} type="button" className={`selection-chip${selected.includes(value) ? ' selected' : ''}`} aria-pressed={selected.includes(value)} onClick={() => toggle(value)}>
            {prefix}{format(value)}
          </button>
        ))}
      </div>
      <input className="selection-range" value={range} onChange={(event) => onRange(event.target.value)} placeholder={hint} aria-label={`${label} range`} spellCheck={false} />
    </div>
  );
}

/** apps/api listSlotMembers(): one ID's on-chain slot row (pointer, upgrade state, host). */
interface SlotMemberRow {
  bdId: string;
  slotNumber: number;
  slotLabel: string;
  slotStatus: 'active';
  virtual: boolean;
  activatedAt: string | null;
  currentCycleNumber: number;
  currentOpenPosition: number;
  nextSlotOpened: boolean;
  upgradeWaiting: boolean;
  placedUnder: string | null;
  placedUnderLevelsUp: number | null;
  enteredViaTreasury: boolean;
  seatsFilled: number;
  activatedSlots: number[];
  directCount: number;
}

interface SlotMembersPage {
  items: SlotMemberRow[];
  totalCount: number;
}

/** Converts a `<input type="datetime-local">` value to a UTC ISO string the API can filter on. */
function localDateTimeToIso(value: string): string | undefined {
  if (!value) return undefined;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString();
}

export function AdminPage() {
  const wallet = useWallet();
  const data = useAppData();
  const [state, setState] = useState<AdminState>('loading');
  const [overview, setOverview] = useState<AdminOverview | null>(null);
  const [audit, setAudit] = useState<AuditRecord[]>([]);
  const [busy, setBusy] = useState<AdminAction | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [explorerInput, setExplorerInput] = useState('');
  const [explorerResult, setExplorerResult] = useState<LookupResult | null>(null);
  const [explorerBusy, setExplorerBusy] = useState(false);
  const [explorerError, setExplorerError] = useState<string | null>(null);
  const [adminSlot, setAdminSlot] = useState<number | null>(null);
  const [adminCycle, setAdminCycle] = useState<number | null>(null);
  const slug = import.meta.env.VITE_ADMIN_ROUTE_SLUG ?? 'operations-7f3c9a';

  const [registrations, setRegistrations] = useState<RegistrationRow[]>([]);
  const [registrationsTotal, setRegistrationsTotal] = useState(0);
  const [registrationsFrom, setRegistrationsFrom] = useState('');
  const [registrationsTo, setRegistrationsTo] = useState('');
  const [registrationsBusy, setRegistrationsBusy] = useState(false);
  const [registrationsError, setRegistrationsError] = useState<string | null>(null);

  const [incomeReport, setIncomeReport] = useState<IncomeReportRow[]>([]);
  const [incomeTotal, setIncomeTotal] = useState(0);
  /** Report-level facts of the on-chain income report: configured level amounts, depth and the paid / missed totals. */
  const [incomeMeta, setIncomeMeta] = useState<{ ratesByDepthAtomic: string[]; levelMaxDepth: number; totals: IncomeReportTotals | null } | null>(null);
  /** The member explorer's sponsor income, read from /income-report/:bdId (SponsorIncome events). */
  const [explorerIncome, setExplorerIncome] = useState<IncomeBreakdown | null>(null);
  const [incomeBusy, setIncomeBusy] = useState(false);
  const [incomeError, setIncomeError] = useState<string | null>(null);
  const [selectedIncomeId, setSelectedIncomeId] = useState<string | null>(null);
  const [incomeBreakdown, setIncomeBreakdown] = useState<IncomeBreakdown | null>(null);
  const [incomeBreakdownBusy, setIncomeBreakdownBusy] = useState(false);
  const [missed, setMissed] = useState<MissedReferralReport | null>(null);
  const [missedBusy, setMissedBusy] = useState(false);
  const [missedError, setMissedError] = useState<string | null>(null);
  const [missedFilter, setMissedFilter] = useState<MissedFilter | null>(null);
  const [treasury, setTreasury] = useState<TreasuryReport | null>(null);
  const [treasuryBusy, setTreasuryBusy] = useState(false);
  const [treasuryError, setTreasuryError] = useState<string | null>(null);
  const [treasuryView, setTreasuryView] = useState<TreasuryView | null>(null);
  /** Live vault bucket balances (bucket name -> atomic), from /api/public/onchain/params; null until read. */
  const [vaultBuckets, setVaultBuckets] = useState<Record<string, string> | null>(null);
  const [vaultBucketsError, setVaultBucketsError] = useState<string | null>(null);
  const [ledgerPage, setLedgerPage] = useState(0);
  const [ledgerPageSize, setLedgerPageSize] = useState(25);
  const [ledgerNewestFirst, setLedgerNewestFirst] = useState(true);
  const [tokenPage, setTokenPage] = useState(0);

  const [slotDistribution, setSlotDistribution] = useState<SlotDistributionRow[]>([]);
  const [selectedReportSlot, setSelectedReportSlot] = useState<number | null>(null);
  const [slotMembers, setSlotMembers] = useState<SlotMemberRow[]>([]);
  const [slotMembersTotal, setSlotMembersTotal] = useState(0);
  const [slotMembersBusy, setSlotMembersBusy] = useState(false);

  // Owner 2026-10-12: every opened slot by default (¼, ½, 1-16) - a Half / Quarter ID holding only its sub-slot must be listed.
  const [browseSlotsSel, setBrowseSlotsSel] = useState<number[]>([]);
  const [browseCyclesSel, setBrowseCyclesSel] = useState<number[]>([]);
  const [browsePositionsSel, setBrowsePositionsSel] = useState<number[]>([]);
  const [browseRange, setBrowseRange] = useState({ slots: '', cycles: '', positions: '' });
  const [browseIdsText, setBrowseIdsText] = useState('');
  const [browseCycles, setBrowseCycles] = useState<number[]>([]);
  const [browsePageSize, setBrowsePageSize] = useState(25);
  const [browseFrom, setBrowseFrom] = useState('');
  const [browseTo, setBrowseTo] = useState('');
  const [browseItems, setBrowseItems] = useState<BrowseMemberRow[]>([]);
  const [browseTotal, setBrowseTotal] = useState(0);
  const [browseBusy, setBrowseBusy] = useState(false);
  const [browseError, setBrowseError] = useState<string | null>(null);
  const [browsePositionsApplied, setBrowsePositionsApplied] = useState<number[] | null>(null);
  const [cardCycles, setCardCycles] = useState<Record<string, number>>({});
  // Owner report 2026-09-27: switching the slot on one ID card must not change the other cards. The global
  // Slots picker still filters the list; a chip on a card only changes that card (fetching the slot if needed).
  const [cardSlot, setCardSlot] = useState<Record<string, number>>({});
  const [cardExtraSlots, setCardExtraSlots] = useState<Record<string, BrowseSlotView>>({});
  const selectCardSlot = async (bdId: string, slotNumber: number, row: BrowseMemberRow) => {
    const key = `${bdId}#${slotNumber}`;
    if (!row.slots.some((slot) => slot.slotNumber === slotNumber) && !cardExtraSlots[key]) {
      try {
        const query = new URLSearchParams({ cursor: '0', limit: '1', ids: bdId, slots: String(slotNumber) });
        const page = await api<BrowseMembersPage>(`/api/${slug}/slots-report/browse?${query.toString()}`);
        const fetched = page.items[0]?.slots.find((slot) => slot.slotNumber === slotNumber);
        if (fetched) setCardExtraSlots((previous) => ({ ...previous, [key]: fetched }));
      } catch {
        return;
      }
    }
    setCardSlot((previous) => ({ ...previous, [bdId]: slotNumber }));
  };
  const [uplineLimitInput, setUplineLimitInput] = useState('24');
  const [routingEvents, setRoutingEvents] = useState<RoutingEventsPage | null>(null);
  const [leadersClub, setLeadersClub] = useState<LeadersClubReport | null>(null);
  const [primaryTokens, setPrimaryTokens] = useState<PrimaryTokenRegister | null>(null);
  const tokenLiquidity = useTokenLiquidity();
  const [testRegs, setTestRegs] = useState<{ enabled: boolean; gasWei: string | null; items: TestRegistration[]; cdtBenefit?: unknown; defaultCountry?: string | null } | null>(null);
  const [testSponsor, setTestSponsor] = useState('BD-1');
  const [testCountry, setTestCountry] = useState('IN');
  const cdtBenefitParams = useCdtBenefitParams();
  const testCountryOptions = cdtBenefitParams?.countries?.length ? cdtBenefitParams.countries.map((row) => normalizeCode(row.country)).sort(compareCountries) : ['IN'];
  const [testCountrySearch, setTestCountrySearch] = useState('');
  const visibleTestCountryOptions = testCountryOptions.filter((code) => code === testCountry || countryMatches(code, null, testCountrySearch));
  const [testBusy, setTestBusy] = useState(false);
  const [testError, setTestError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<TestRegisterResult | null>(null);
  const [showTestKey, setShowTestKey] = useState(false);
  /** The test-register POST failed without an API reply (TypeError "Failed to fetch", abort, non-JSON): the server may still have created the ID. */
  const [testDropped, setTestDropped] = useState<{ clickedAt: number; knownIds: number[]; checking: boolean; checks: number } | null>(null);
  /** The register row found after a dropped connection (the key was never delivered - only the original reply carries it). */
  const [testRecovered, setTestRecovered] = useState<TestRegistration | null>(null);
  const testRecheckTimers = useRef<number[]>([]);
  const [primaryTokensError, setPrimaryTokensError] = useState<string | null>(null);
  const [primaryTokenFilter, setPrimaryTokenFilter] = useState('');
  const [leadersClubBusy, setLeadersClubBusy] = useState(false);
  const [leadersClubError, setLeadersClubError] = useState<string | null>(null);
  const [openChainClub, setOpenChainClub] = useState<number | null>(null);
  const [clubVaultAtomic, setClubVaultAtomic] = useState<string | null>(null);
  const [clubPeriodInput, setClubPeriodInput] = useState('28');
  const [clubBusy, setClubBusy] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsError, setSettingsError] = useState<string | null>(null);
  const browseSentinelRef = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    setError(null);
    // The read-only report surface is deliberately public within the hidden slug (see app.ts) —
    // no wallet is required to browse; only mutation actions gate on wallet.authenticated below.
    try {
      const [nextOverview, nextAudit] = await Promise.all([
        api<AdminOverview>(`/api/${slug}/overview`),
        api<AuditPage>(`/api/${slug}/audit?cursor=0&limit=20`),
      ]);
      setOverview(nextOverview);
      setAudit(nextAudit.items);
      setState(nextOverview.authorized ? 'authorized' : 'denied');
    } catch (cause) {
      if (cause instanceof ApiError && (cause.status === 403 || cause.status === 404)) {
        setState('denied');
        return;
      }
      setError(cause instanceof Error ? cause.message : 'Admin data could not be loaded.');
      setState('authorized');
    }
  }, [slug]);

  useEffect(() => {
    void load();
    const interval = window.setInterval(() => void load(), 15_000);
    return () => window.clearInterval(interval);
  }, [load]);

  const loadRegistrations = useCallback(async (from?: string, to?: string) => {
    setRegistrationsBusy(true);
    setRegistrationsError(null);
    try {
      const query = new URLSearchParams({ cursor: '0', limit: '100' });
      if (from) query.set('from', from);
      if (to) query.set('to', to);
      const page = await api<RegistrationsPage>(`/api/${slug}/members?${query.toString()}`);
      setRegistrations(page.items);
      setRegistrationsTotal(page.totalCount);
    } catch (cause) {
      setRegistrationsError(cause instanceof Error ? cause.message : 'Registrations could not be loaded.');
    } finally {
      setRegistrationsBusy(false);
    }
  }, [slug]);

  const loadIncomeReport = useCallback(async () => {
    setIncomeBusy(true);
    setIncomeError(null);
    try {
      const page = await api<IncomeReportPage>(`/api/${slug}/income-report?cursor=0&limit=100`);
      setIncomeReport(page.items);
      setIncomeTotal(page.totalCount);
      setIncomeMeta({ ratesByDepthAtomic: page.ratesByDepthAtomic ?? [], levelMaxDepth: page.levelMaxDepth, totals: page.totals ?? null });
    } catch (cause) {
      setIncomeError(cause instanceof Error ? cause.message : 'Income report could not be loaded.');
    } finally {
      setIncomeBusy(false);
    }
  }, [slug]);

  const openIncomeBreakdown = useCallback(async (bdId: string) => {
    setSelectedIncomeId(bdId);
    setIncomeBreakdown(null);
    setIncomeBreakdownBusy(true);
    try {
      setIncomeBreakdown(await api<IncomeBreakdown>(`/api/${slug}/income-report/${encodeURIComponent(bdId)}`));
    } catch (cause) {
      setIncomeError(cause instanceof Error ? cause.message : 'Income breakdown could not be loaded.');
    } finally {
      setIncomeBreakdownBusy(false);
    }
  }, [slug]);

  const loadMissedReferral = useCallback(async () => {
    setMissedBusy(true);
    setMissedError(null);
    try {
      setMissed(await api<MissedReferralReport>(`/api/${slug}/missed-referral-income`));
    } catch (cause) {
      setMissedError(cause instanceof Error ? cause.message : 'Missed referral income could not be loaded.');
    } finally {
      setMissedBusy(false);
    }
  }, [slug]);

  const loadTestRegistrations = useCallback(async () => {
    try {
      const next = await api<{ enabled: boolean; gasWei: string | null; items: TestRegistration[]; cdtBenefit?: unknown; defaultCountry?: string | null }>(`/api/${slug}/test-registrations`);
      setTestRegs(next);
      if (next.defaultCountry) setTestCountry((value) => (value === 'IN' ? normalizeCode(next.defaultCountry) : value));
      return next;
    } catch {
      setTestRegs(null);
      return null;
    }
  }, [slug]);

  const clearTestRecheckTimers = useCallback(() => {
    testRecheckTimers.current.forEach((id) => window.clearTimeout(id));
    testRecheckTimers.current = [];
  }, []);
  useEffect(() => clearTestRecheckTimers, [clearTestRecheckTimers]);

  /**
   * After a dropped connection: re-read the test register, the registrations table and the overview counters, then look
   * for a row that was not listed before the click and was created at (or after) the click. Found -> success line.
   */
  const recheckDroppedRegistration = useCallback(async (clickedAt: number, knownIds: number[]) => {
    setTestDropped((value) => (value ? { ...value, checking: true } : value));
    const [next] = await Promise.all([loadTestRegistrations(), loadRegistrations(), load()]);
    const found = (next?.items ?? [])
      .filter((item) => !knownIds.includes(item.id) && new Date(item.createdAt).getTime() >= clickedAt - TEST_REGISTER_CLOCK_SKEW_MS)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())[0] ?? null;
    if (found) {
      clearTestRecheckTimers();
      setTestRecovered(found);
      setTestDropped(null);
      setNotice(`Registration completed: ${found.bdId} under ${found.sponsorBdId} with wallet ${found.wallet} (country ${found.country ? countryLabel(normalizeCode(found.country)) : 'not sent'}).`);
    } else {
      setTestDropped((value) => (value ? { ...value, checking: false, checks: value.checks + 1 } : value));
    }
    return found;
  }, [loadTestRegistrations, loadRegistrations, load, clearTestRecheckTimers]);

  const runTestRegister = useCallback(async () => {
    if (!/^BD-\d{1,12}$/i.test(testSponsor.trim())) { setTestError('Enter a sponsor ID like BD-1.'); return; }
    clearTestRecheckTimers();
    setTestBusy(true); setTestError(null); setTestResult(null); setShowTestKey(false); setTestDropped(null); setTestRecovered(null);
    const clickedAt = Date.now();
    const knownIds = testRegs?.items.map((item) => item.id) ?? [];
    try {
      const result = await api<TestRegisterResult>(`/api/${slug}/test-register`, {
        method: 'POST',
        headers: wallet.csrfToken ? { 'X-CSRF-Token': wallet.csrfToken } : {},
        body: JSON.stringify({ sponsorId: testSponsor.trim().toUpperCase(), country: testCountry }),
        // The handler sends 4-5 transactions (20-40 s, longer on a slow RPC): never let the browser give up before the server.
        ...(typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function' ? { signal: AbortSignal.timeout(TEST_REGISTER_TIMEOUT_MS) } : {}),
      });
      if (!result || typeof result !== 'object' || !result.bdId) throw new TypeError('The API reply was not readable.');
      setTestResult(result);
      setNotice(`Registered ${result.bdId} under ${result.sponsorBdId} with wallet ${result.wallet}.`);
      await Promise.all([loadTestRegistrations(), loadRegistrations()]);
    } catch (cause) {
      if (cause instanceof ApiError) {
        // The API answered with an error: the registration did not happen (or was rejected).
        setTestError(cause.message);
      } else {
        // No API reply (TypeError "Failed to fetch", AbortError, unreadable body): the server usually finishes the work, so
        // instead of a red error re-read the registers at TEST_REGISTER_RECHECK_DELAYS_MS and report the row that appears.
        setTestDropped({ clickedAt, knownIds, checking: false, checks: 0 });
        testRecheckTimers.current = TEST_REGISTER_RECHECK_DELAYS_MS.map((delay) => window.setTimeout(() => void recheckDroppedRegistration(clickedAt, knownIds), delay));
      }
    } finally {
      setTestBusy(false);
    }
  }, [wallet.csrfToken, testSponsor, testCountry, testRegs, slug, loadTestRegistrations, loadRegistrations, clearTestRecheckTimers, recheckDroppedRegistration]);

  const loadPrimaryTokens = useCallback(async () => {
    setPrimaryTokensError(null);
    try {
      setPrimaryTokens(await api<PrimaryTokenRegister>(`/api/${slug}/primary-tokens`));
    } catch (cause) {
      setPrimaryTokensError(cause instanceof Error ? cause.message : 'Primary token register could not be loaded.');
    }
  }, [slug]);

  const loadLeadersClub = useCallback(async () => {
    setLeadersClubBusy(true);
    setLeadersClubError(null);
    try {
      // The live vault LEADERS_CLUB bucket (RPC read) is best-effort: the indexed report still renders without it.
      const [report, params] = await Promise.all([
        api<LeadersClubReport>(`/api/${slug}/leaders-club`),
        api<EngineParameters>('/api/public/onchain/params').catch(() => null),
      ]);
      setLeadersClub(report);
      setClubVaultAtomic(params?.vault?.buckets.find((bucket) => bucket.bucket === 'LEADERS_CLUB')?.balanceAtomic ?? null);
    } catch (cause) {
      setLeadersClubError(cause instanceof Error ? cause.message : 'Leaders Club report could not be loaded.');
    } finally {
      setLeadersClubBusy(false);
    }
  }, [slug]);

  useEffect(() => {
    if (leadersClub) setClubPeriodInput(String(leadersClub.periodDays));
  }, [leadersClub]);

  // Owner 2026-10-11: the Leaders Club period and its settlements exist only on-chain (BDynamicLeadersClub). The period is
  // set with setPeriod by the contract owner's wallet; settle() can be sent by any wallet once due. No server setting.
  const saveClubPeriod = useCallback(async (calendarMonth = false) => {
    if (!wallet.address) {
      setLeadersClubError('Connect the governance (Leaders Club owner) wallet to change the period on the contract.');
      return;
    }
    const value = Number(clubPeriodInput);
    if (!calendarMonth && (!Number.isInteger(value) || value < 1 || value > 366)) {
      setLeadersClubError('Enter a whole number of days between 1 and 366.');
      return;
    }
    setClubBusy(true);
    setLeadersClubError(null);
    try {
      const hash = await sendGovernanceCall(resolveGovernanceAddresses(), wallet.address as `0x${string}`, 'leadersClub', LEADERS_CLUB_ABI, 'setPeriod', calendarMonth ? [1, 0n] : [0, BigInt(value) * 86_400n]);
      setNotice(`Leaders Club setPeriod sent (${calendarMonth ? 'calendar month, 12 midnight IST' : `every ${value} day${value === 1 ? '' : 's'}`}): ${hash}. The panel follows once the contract emits PeriodConfigured.`);
      window.setTimeout(() => void loadLeadersClub(), 15_000);
    } catch (cause) {
      setLeadersClubError(cause instanceof Error ? cause.message : 'The contract rejected the period change (only the owner may set it).');
    } finally {
      setClubBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubPeriodInput, wallet.address, slug]);

  const runClubSettlement = useCallback(async () => {
    if (!wallet.address) {
      setLeadersClubError('Connect a wallet to send the on-chain settle() (it only pays the gas).');
      return;
    }
    setClubBusy(true);
    setLeadersClubError(null);
    try {
      const hash = await sendGovernanceCall(resolveGovernanceAddresses(), wallet.address as `0x${string}`, 'leadersClub', LEADERS_CLUB_ABI, 'settle', []);
      setNotice(`Leaders Club settle() sent: ${hash}. Payouts appear once the indexer sees ClubSettled / ClubPaid.`);
      window.setTimeout(() => void loadLeadersClub(), 15_000);
    } catch (cause) {
      setLeadersClubError(cause instanceof Error ? cause.message : 'The contract rejected settle() (not due yet?).');
    } finally {
      setClubBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wallet.address, slug]);

  const loadTreasury = useCallback(async () => {
    setTreasuryBusy(true);
    setTreasuryError(null);
    // Live vault buckets are read alongside (not awaited) so a slow chain read never holds up the report.
    void api<EngineParameters>('/api/public/onchain/params').then((params) => {
      if (params.vault) {
        setVaultBuckets(Object.fromEntries(params.vault.buckets.map((row) => [row.bucket, row.balanceAtomic])));
        setVaultBucketsError(null);
      } else {
        setVaultBuckets(null);
        setVaultBucketsError(params.reason ?? 'vault not configured');
      }
    }).catch((cause: unknown) => {
      setVaultBuckets(null);
      setVaultBucketsError(cause instanceof Error ? cause.message : 'live vault read failed');
    });
    try {
      setTreasury(await api<TreasuryReport>(`/api/${slug}/treasury`));
    } catch (cause) {
      setTreasuryError(cause instanceof Error ? cause.message : 'Treasury report could not be loaded.');
    } finally {
      setTreasuryBusy(false);
    }
  }, [slug]);

  useEffect(() => {
    setUplineLimitInput(String(data.placementRules.uplineSearchLimit));
  }, [data.placementRules.uplineSearchLimit]);

  // A treasury box click opens its detail right under the boxes; bring it into view on phones.
  useEffect(() => {
    if (!treasuryView) return;
    window.setTimeout(() => document.getElementById('treasury-detail')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  }, [treasuryView]);

  // Owner 2026-10-11: the senior sponsor check limit is BDynamicMatrix.uplineSearchLimit - changed with setRoutingLimits
  // signed by the matrix owner's wallet (downline generations / branches are kept at their live values).
  const saveUplineLimit = useCallback(async () => {
    if (!wallet.address) {
      setSettingsError('Connect the governance (matrix owner) wallet to change the limit on the contract.');
      return;
    }
    const value = Number(uplineLimitInput);
    if (!Number.isInteger(value) || value < 1 || value > 500) {
      setSettingsError('Enter a whole number between 1 and 500.');
      return;
    }
    setSettingsBusy(true);
    setSettingsError(null);
    try {
      const hash = await sendGovernanceCall(resolveGovernanceAddresses(), wallet.address as `0x${string}`, 'matrix', MATRIX_ABI, 'setRoutingLimits', [value, data.placementRules.downlineDepth, data.placementRules.downlineBranches]);
      setNotice(`BDynamicMatrix.setRoutingLimits(${value}, ${data.placementRules.downlineDepth}, ${data.placementRules.downlineBranches}) sent: ${hash}. Every panel follows once the indexer sees RoutingLimitsConfigured.`);
      window.setTimeout(() => data.refresh(), 15_000);
    } catch (cause) {
      setSettingsError(cause instanceof Error ? cause.message : 'The contract rejected the change (only the matrix owner may set it).');
    } finally {
      setSettingsBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uplineLimitInput, wallet.address, data.placementRules.downlineDepth, data.placementRules.downlineBranches, data.refresh]);

  const loadSlotDistribution = useCallback(async () => {
    try {
      const result = await api<{ items: SlotDistributionRow[] }>(`/api/${slug}/slots-report`);
      setSlotDistribution(result.items);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Slot distribution could not be loaded.');
    }
  }, [slug]);

  const openSlotMembers = useCallback(async (slotNumber: number) => {
    setSelectedReportSlot(slotNumber);
    setSlotMembersBusy(true);
    try {
      // limit must stay within HTTP.maxPageSize (100) or the API rejects the request with a 422
      // and the list silently renders empty while the tile still shows a non-zero count.
      const page = await api<SlotMembersPage>(`/api/${slug}/slots-report/${slotNumber}?cursor=0&limit=100`);
      setSlotMembers(page.items);
      setSlotMembersTotal(page.totalCount);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Slot member list could not be loaded.');
    } finally {
      setSlotMembersBusy(false);
    }
  }, [slug]);

  const selectionText = (chips: number[], range: string) => [chips.join(','), range.trim()].filter(Boolean).join(',');
  // Empty = every slot, the Half / Quarter sub-slots (keys 50 = ½, 25 = ¼) included.
  const browseSlotsText = selectionText(browseSlotsSel, browseRange.slots) || '25,50,1-16';
  const browseCyclesText = selectionText(browseCyclesSel, browseRange.cycles);
  const browsePositionsText = selectionText(browsePositionsSel, browseRange.positions);

  const loadBrowsePage = useCallback(async (reset: boolean) => {
    setBrowseBusy(true);
    setBrowseError(null);
    try {
      const cursor = reset ? 0 : browseItems.length;
      const query = new URLSearchParams({ cursor: String(cursor), limit: String(browsePageSize), slots: browseSlotsText });
      if (browseCyclesText) query.set('cycles', browseCyclesText);
      if (browsePositionsText) query.set('positions', browsePositionsText);
      if (browseIdsText.trim()) query.set('ids', browseIdsText.trim());
      const from = localDateTimeToIso(browseFrom);
      const to = localDateTimeToIso(browseTo);
      if (from) query.set('from', from);
      if (to) query.set('to', to);
      const page = await api<BrowseMembersPage>(`/api/${slug}/slots-report/browse?${query.toString()}`);
      setBrowseItems((previous) => (reset ? page.items : [...previous, ...page.items]));
      setBrowseTotal(page.totalCount);
      setBrowseCycles(page.availableCycles);
      setBrowsePositionsApplied(page.positionsSelected);
    } catch (cause) {
      setBrowseError(cause instanceof Error ? cause.message : 'Could not load IDs for this selection.');
    } finally {
      setBrowseBusy(false);
    }
  }, [browseCyclesText, browseFrom, browseIdsText, browseItems.length, browsePageSize, browsePositionsText, browseSlotsText, browseTo, slug]);

  // Every filter reloads on its own (debounced so typing an ID range does not fire per keystroke).
  useEffect(() => {
    if (state !== 'authorized') return;
    const timer = window.setTimeout(() => void loadBrowsePage(true), 300);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browseSlotsText, browseCyclesText, browsePositionsText, browseIdsText, browseFrom, browseTo, browsePageSize, state]);

  // Switching slot can shrink the card grid from dozens of rows to none. Without this, a viewer who
  // clicked a slot chip deep in the list is left staring at the blank space the grid used to fill.
  const browseSlotFirstRender = useRef(true);
  useEffect(() => {
    if (browseSlotFirstRender.current) {
      browseSlotFirstRender.current = false;
      return;
    }
    document.getElementById('admin-id-browser')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [browseSlotsText]);

  const submitBrowseFilter = (event: FormEvent) => {
    event.preventDefault();
    void loadBrowsePage(true);
  };

  const clearBrowseFilters = () => {
    setBrowseSlotsSel([]);
    setBrowseCyclesSel([]);
    setBrowsePositionsSel([]);
    setBrowseRange({ slots: '', cycles: '', positions: '' });
    setBrowseIdsText('');
    setBrowseFrom('');
    setBrowseTo('');
  };

  const browseOnlySlot = (slotNumber: number) => {
    setBrowseSlotsSel([slotNumber]);
    setBrowseRange((previous) => ({ ...previous, slots: '' }));
  };
  const browseHasFilters = browseSlotsSel.length > 0 || browseCyclesText !== '' || browsePositionsText !== '' || browseIdsText.trim() !== '' || browseFrom !== '' || browseTo !== '';

  useEffect(() => {
    const sentinel = browseSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver((entries) => {
      const visible = entries[0]?.isIntersecting;
      if (visible && !browseBusy && browseItems.length < browseTotal) {
        void loadBrowsePage(false);
      }
    }, { rootMargin: '200px' });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [browseBusy, browseItems.length, browseTotal, loadBrowsePage]);

  useEffect(() => {
    if (state !== 'authorized') return;
    void loadRegistrations();
    void loadIncomeReport();
    void loadMissedReferral();
    void loadTreasury();
    void loadLeadersClub();
    void loadPrimaryTokens();
    void loadTestRegistrations();
    void loadSlotDistribution();
  }, [state, loadRegistrations, loadIncomeReport, loadMissedReferral, loadTreasury, loadSlotDistribution, loadPrimaryTokens, loadTestRegistrations]);

  const submitRegistrationFilter = (event: FormEvent) => {
    event.preventDefault();
    void loadRegistrations(localDateTimeToIso(registrationsFrom), localDateTimeToIso(registrationsTo));
  };

  const clearRegistrationFilter = () => {
    setRegistrationsFrom('');
    setRegistrationsTo('');
    void loadRegistrations();
  };

  const perform = async (action: AdminAction, path: string) => {
    if (!wallet.csrfToken) {
      setError('Re-authenticate the wallet to obtain a fresh CSRF token before an admin mutation.');
      return;
    }
    if (action === 'rebuild' && !window.confirm('Rebuild every indexed read model from the finalized retained chain events?')) return;
    setBusy(action);
    setError(null);
    setNotice(null);
    try {
      await api(`/api/${slug}${path}`, {
        method: 'POST',
        headers: { 'X-CSRF-Token': wallet.csrfToken },
      });
      setNotice(`${action} completed successfully.`);
      data.refresh();
      await load();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : `${action} failed.`);
    } finally {
      setBusy(null);
    }
  };

  const runExplorer = async (event: FormEvent) => {
    event.preventDefault();
    await runExplorerFor(explorerInput);
  };

  const runExplorerFor = async (raw: string) => {
    const clean = raw.trim();
    if (!clean) return;
    document.getElementById('admin-explorer')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    setExplorerBusy(true);
    setExplorerError(null);
    try {
      const found = await api<LookupResult>(`/api/${slug}/lookup?q=${encodeURIComponent(clean)}`);
      setExplorerResult(found);
      setRoutingEvents(null);
      setExplorerIncome(null);
      if (found.kind === 'member') {
        api<RoutingEventsPage>(`/api/${slug}/routing-events?id=${encodeURIComponent(found.member.bdId)}&cursor=0&limit=100`)
          .then(setRoutingEvents)
          .catch(() => setRoutingEvents({ items: [], totalCount: 0 }));
        api<IncomeBreakdown>(`/api/${slug}/income-report/${encodeURIComponent(found.member.bdId)}`)
          .then(setExplorerIncome)
          .catch(() => setExplorerIncome(null));
      }
      // A Half / Quarter ID has no member_slots row active at first: fall back to its first slot view (Slot ½ / ¼).
      const firstSlot = found.kind === 'member' ? (found.explorer.slots.find((slot) => slot.status !== 'locked')?.slotNumber ?? found.explorer.projectedSlots[0]?.slotNumber ?? 1) : null;
      setAdminSlot(firstSlot);
      setAdminCycle(null);
    } catch (cause) {
      setExplorerResult(null);
      setAdminSlot(null);
      setAdminCycle(null);
      setExplorerError(
        cause instanceof ApiError && cause.status === 404
          ? `No indexed record matches "${clean}".`
          : cause instanceof Error
            ? cause.message
            : 'Record lookup failed.',
      );
    } finally {
      setExplorerBusy(false);
    }
  };

  const explorerMember = explorerResult?.kind === 'member' ? explorerResult.explorer : null;
  const adminSlotCycles = useMemo(
    () => (explorerMember && adminSlot !== null ? explorerMember.cycles.filter((cycle) => cycle.slotNumber === adminSlot) : []),
    [explorerMember, adminSlot],
  );
  // The placement engine projects every slot the member has entered (rules/rule7.txt).
  const adminProjectedSlot = useMemo(
    () => (explorerMember && adminSlot !== null ? explorerMember.projectedSlots.find((slot) => slot.slotNumber === adminSlot) ?? null : null),
    [explorerMember, adminSlot],
  );
  const adminProjectedCycles = useMemo(() => adminProjectedSlot?.cycles ?? [], [adminProjectedSlot]);
  const adminAvailableCycles = useMemo(() => {
    const numbers = new Set<number>([...adminSlotCycles.map((cycle) => cycle.cycleNumber), ...adminProjectedCycles.map((cycle) => cycle.cycleNumber)]);
    return [...numbers].sort((a, b) => a - b);
  }, [adminSlotCycles, adminProjectedCycles]);
  const adminSelectedCycleNumber = adminCycle ?? adminAvailableCycles[adminAvailableCycles.length - 1] ?? 1;
  const adminRealCycle = adminSlotCycles.find((cycle) => cycle.cycleNumber === adminSelectedCycleNumber) ?? null;
  const adminProjectedCycle = adminProjectedCycles.find((cycle) => cycle.cycleNumber === adminSelectedCycleNumber) ?? null;
  const adminIsProjected = !adminRealCycle && adminProjectedCycle !== null;
  const adminDisplayCycle: CycleDetail | null =
    adminRealCycle ??
    (adminProjectedCycle
      ? {
          slotNumber: adminSlot ?? 1,
          cycleNumber: adminProjectedCycle.cycleNumber,
          status: 'active',
          totalPositions: 12,
          completedPositions: 0,
          startedAt: '',
          completedAt: null,
          placements: [],
        }
      : null);
  const adminMatrixNodes = useMemo(() => {
    if (adminRealCycle) return buildAdminMatrixNodes(adminRealCycle);
    if (adminProjectedCycle) return buildAdminProjectedMatrixNodes(adminProjectedCycle);
    return [];
  }, [adminRealCycle, adminProjectedCycle]);

  const rpc = overview?.rpc ?? data.rpcProviders.map((provider) => ({
    id: provider.id,
    priority: provider.priority,
    healthy: provider.state === 'healthy',
    latencyMs: provider.latency,
    consecutiveFailures: provider.failures,
    lastCheckedAt: provider.lastCheckedAt,
  }));
  const healthyRpc = rpc.filter((provider) => provider.healthy).length;
  const csv = useMemo(() => [
    ['time', 'actor', 'action', 'resource', 'resourceId', 'outcome'],
    ...audit.map((entry) => [entry.createdAt, entry.actorWallet ?? '', entry.action, entry.resource, entry.resourceId ?? '', entry.outcome]),
  ].map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n'), [audit]);

  const exportAudit = () => {
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'bdynamic-admin-audit.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  // 'loading' must not fall through to the 404 view, or the console flashes "Page not found" on
  // every load until the first fetch settles.
  if (state === 'loading') {
    return <section className="neutral-not-found"><LoaderCircle className="spin" size={30} /><h1>Loading console</h1><p>Reading the indexed chain data.</p></section>;
  }

  if (state !== 'authorized') {
    return <section className="neutral-not-found"><span>404</span><h1>Page not found</h1><p>The requested resource does not exist.</p>{wallet.address && !wallet.authenticated ? <button className="button secondary" type="button" onClick={() => void wallet.authenticate()}><KeyRound size={15} /> Verify session</button> : null}</section>;
  }

  return (
    <div className="admin-console">
      <PageHeading eyebrow="AUTHORIZED OPERATIONS" title="Protocol control room" description="Live observation and bounded indexer operations. Economic history cannot be edited here." actions={<><StatusPill tone="amber"><LockKeyhole size={12} /> CSRF-protected writes</StatusPill><StatusPill tone={wallet.authenticated ? 'green' : 'violet'}><ShieldCheck size={12} /> {wallet.authenticated ? 'Admin session' : 'Read-only (connect admin wallet to act)'}</StatusPill></>} />
      <div className="admin-warning"><AlertTriangle /><div><strong>{wallet.expectedChainId === 56 ? 'Mainnet guard active' : 'Testnet operations'}</strong><span>Every available mutation maps to a protected API route and is written to the append-only audit log.</span></div><code>CHAIN {wallet.expectedChainId}</code></div>
      {error ? <div className="transaction-callout error" role="alert"><AlertTriangle /><div><strong>Operation stopped</strong><p>{error}</p></div></div> : null}
      {notice ? <div className="transaction-callout success" role="status"><CheckCircle2 /><div><strong>{notice}</strong></div></div> : null}

      <section className="panel admin-explorer" id="admin-explorer">
        <div className="panel-heading"><div><span className="eyebrow">RAW RECORD LOOKUP</span><h2>Member explorer</h2></div><StatusPill tone="amber"><LockKeyhole size={11} /> Admin-only fields</StatusPill></div>
        <form className="admin-explorer-form" onSubmit={(event) => void runExplorer(event)}>
          <label><Search size={15} /><input value={explorerInput} onChange={(event) => setExplorerInput(event.target.value)} placeholder="BD-1, 0x wallet, or transaction hash" autoComplete="off" spellCheck={false} /></label>
          <button className="button secondary small" type="submit" disabled={explorerBusy || !explorerInput.trim()}>{explorerBusy ? 'Searching...' : 'Search'}</button>
        </form>
        {explorerError ? <p className="empty-copy">{explorerError}</p> : null}
        {explorerResult?.kind === 'transaction' ? (
          <div className="admin-kv">
            <span>Tx hash</span><code>{explorerResult.transaction.txHash}</code>
            <span>Status</span><span>{explorerResult.transaction.status}</span>
            <span>Chain</span><span>{explorerResult.transaction.chainId}</span>
            <span>Block</span><span>{explorerResult.transaction.blockNumber ?? 'pending'}</span>
            <span>Observed</span><span>{explorerResult.transaction.observedAt}</span>
          </div>
        ) : null}
        {explorerResult?.kind === 'member' ? (
          <>
            <div className="admin-kv">
              <span>Member ID</span><code>{explorerResult.member.bdId}</code>
              <span>Wallet</span><code>{explorerResult.member.walletAddress}</code>
              <span>Sponsor</span><code>{explorerResult.member.sponsorBdId ?? 'root'}</code>
              <span>Era</span><span>{explorerResult.member.birthEra}</span>
              <span>Rank</span><span>{explorerResult.member.rank}</span>
              <span>Status</span><span>{explorerResult.member.status}</span>
              <span>Joined</span><span>{explorerResult.member.joinedAt}</span>
              <span>Direct / team</span><span>{explorerResult.explorer.stats.directMembers} / {explorerResult.explorer.stats.teamMembers}</span>
              <span>Completed cycles</span><span>{explorerResult.explorer.stats.completedCycles}</span>
              <span>Active slots</span><span>{explorerResult.explorer.stats.activeSlots} / 16</span>
              <span>Total earned (atomic)</span><code>{explorerResult.explorer.stats.totalEarnedAtomic}</code>
              <span>Redeemable (atomic)</span><code>{explorerResult.explorer.stats.redeemableAtomic}</code>
              <span>Sponsor income: level 1 (atomic)</span><span>{explorerIncome ? `${explorerIncome.byLevel[0]?.count ?? 0} payment${explorerIncome.byLevel[0]?.count === 1 ? '' : 's'} = ${explorerIncome.byLevel[0]?.amountAtomic ?? '0'}` : '…'}</span>
              <span>Sponsor income: levels 2-{explorerIncome?.byLevel.length ?? 10} (atomic)</span><span>{explorerIncome ? `${explorerIncome.byLevel.slice(1).reduce((sum, level) => sum + level.count, 0)} payments = ${explorerIncome.byLevel.slice(1).reduce((sum, level) => sum + BigInt(level.amountAtomic), 0n).toString()}` : '…'}</span>
              <span>Sponsor income received (atomic)</span><code>{explorerIncome ? `${explorerIncome.receivedTotalAtomic} gross · ${explorerIncome.netReceivedAtomic} net` : '…'}</code>
            </div>
            <p className="empty-copy">Sponsor income above is what the Matrix contract actually paid this ID (indexed SponsorIncome events; net = the vault's RewardPaid after its sponsor fee). The per-payment list is in the Sponsor income report below.</p>
            <PrimaryTokenCard token={explorerResult.explorer.primaryToken} />
            <CdtBenefitCard bdId={explorerResult.member.bdId} token={explorerResult.explorer.primaryToken} view={explorerResult.explorer.cdtBenefit} readOnly />
            <OnChainMatrixSection bdId={explorerResult.member.bdId} compact />
            <GenerationIncomeMap bdId={explorerResult.member.bdId} compact onSelectId={(id) => { setExplorerInput(id); void runExplorerFor(id); }} />

            <div className="admin-scroll">
              <div className="ledger-head"><span>Slot</span><span>Type</span><span>Status</span><span>Required (atomic)</span><span>Activated / completed</span></div>
              {explorerResult.explorer.slots.map((slot) => (
                <div className="ledger-row" key={slot.slotNumber}>
                  <span>{slot.slotNumber}</span>
                  <span>{slot.slotType}</span>
                  <StatusPill tone={slot.status === 'completed' ? 'green' : slot.status === 'active' ? 'violet' : 'neutral'}>{slot.status}</StatusPill>
                  <code>{slot.requiredAmountAtomic}</code>
                  <span>{slot.activatedAt ?? '—'} → {slot.completedAt ?? '—'}</span>
                </div>
              ))}
            </div>

            <div className="panel-heading admin-subhead"><div><span className="eyebrow">DOWNLINE (RAW)</span><h3><Users size={13} /> {explorerResult.explorer.networkTotalCount.toLocaleString()} nodes{explorerResult.explorer.networkTruncated ? ' (truncated)' : ''}</h3></div></div>
            <div className="admin-scroll">
              <div className="ledger-head"><span>Depth</span><span>Member</span><span>Sponsor</span><span>Era / status</span><span>Confirmed (atomic)</span></div>
              {explorerResult.explorer.network.nodes.map((node) => (
                <div className="ledger-row" key={node.bdId}>
                  <span>{'·'.repeat(node.depth)} {node.depth}</span>
                  <code>{node.bdId}</code>
                  <code>{node.sponsorBdId ?? 'root'}</code>
                  <span>{node.birthEra} / {node.status}</span>
                  <code>{node.confirmedVolumeAtomic}</code>
                </div>
              ))}
            </div>

            {explorerResult.explorer.upline.length > 0 ? (
              <>
                <div className="panel-heading admin-subhead"><div><span className="eyebrow">UPLINE (RAW)</span><h3><GitBranch size={13} /> {explorerResult.explorer.upline.length} generation{explorerResult.explorer.upline.length === 1 ? '' : 's'} to root</h3></div></div>
                <div className="ledger-head"><span>Gen.</span><span>Sponsor</span><span>Era</span><span>Status</span><span>Joined</span></div>
                {explorerResult.explorer.upline.map((node) => (
                  <div className="ledger-row" key={node.bdId}><span>{node.generationsAbove}</span><code>{node.bdId}</code><span>{node.birthEra}</span><span>{node.status}</span><span>{node.joinedAt}</span></div>
                ))}
              </>
            ) : null}

            <div className="panel-heading admin-subhead"><div><span className="eyebrow">SLOTS × CYCLES (RAW)</span><h3>{explorerResult.explorer.slots.filter((s) => s.status !== 'locked').length}/16 active slots · {explorerResult.explorer.cycles.length} cycle{explorerResult.explorer.cycles.length === 1 ? '' : 's'} total</h3></div></div>
            <div className="admin-slot-picker">
              {/* Half / Quarter era: the ID's Slot ¼ / ½ (on-chain views, never in member_slots) come before Slot 1. */}
              {SUB_SLOTS.map((slotNumber) => explorerResult.explorer.projectedSlots.find((item) => item.slotNumber === slotNumber)).filter((item): item is ProjectedSlotView => item !== undefined).map((sub) => (
                <button
                  key={sub.slotNumber}
                  type="button"
                  className={`admin-slot-chip active${sub.virtual ? ' virtual' : ''}${adminSlot === sub.slotNumber ? ' selected' : ''}`}
                  onClick={() => { setAdminSlot(sub.slotNumber); setAdminCycle(null); }}
                  title={`${slotTitle(sub.slotNumber, sub.virtual === true)}: ${sub.source === 'onchain' ? 'activated on-chain' : 'not confirmed on-chain'} · cycle ${sub.currentCycleNumber}${sub.virtual ? ' (seats lower-era IDs, no payment, no upgrade)' : ''}`}
                >
                  <strong>{slotLabel(sub.slotNumber)}</strong>
                  <small>{sub.virtual ? 'virtual' : `${sub.cycles.length}c`}</small>
                </button>
              ))}
              {explorerResult.explorer.slots.map((slot) => {
                const count = explorerResult.explorer.cycles.filter((c) => c.slotNumber === slot.slotNumber).length;
                // The API's on-chain slot view of this slot (seats from SeatFilled); only source 'onchain' counts.
                const view = explorerResult.explorer.projectedSlots.find((item) => item.slotNumber === slot.slotNumber && item.source === 'onchain');
                const opened = slot.status !== 'locked' || view !== undefined;
                return (
                  <button
                    key={slot.slotNumber}
                    type="button"
                    disabled={!opened}
                    className={`admin-slot-chip${opened ? ' active' : ''}${adminSlot === slot.slotNumber ? ' selected' : ''}`}
                    onClick={() => { setAdminSlot(slot.slotNumber); setAdminCycle(null); }}
                    title={`Slot ${slot.slotNumber}: ${opened ? 'activated on-chain' : 'not activated on-chain'}${view ? ` · cycle ${view.currentCycleNumber}` : ''}, ${count} cycle${count === 1 ? '' : 's'} indexed`}
                  >
                    <strong>{slot.slotNumber}</strong>
                    <small>{count || (view ? `${view.cycles.length}c` : 0)}</small>
                  </button>
                );
              })}
            </div>

            {adminSlot !== null ? (
              <div className="admin-kv" style={{ gridTemplateColumns: '170px 1fr', marginBottom: 14 }}>
                <span>Selected slot</span><code>{slotLabel(adminSlot)}{adminProjectedSlot?.virtual ? ' (virtual)' : ''} · {explorerResult.explorer.slots.find((s) => s.slotNumber === adminSlot)?.slotType ?? (adminProjectedSlot ? explorerResult.member.birthEra : '—')} · {explorerResult.explorer.slots.find((s) => s.slotNumber === adminSlot)?.status ?? (adminProjectedSlot?.source === 'onchain' ? 'active (on-chain)' : '—')}</code>
                <span>Cycles in this slot</span><code>{adminProjectedSlot?.source === 'onchain' ? `${adminProjectedCycles.length} on-chain (SeatFilled)` : `${adminSlotCycles.length} on-chain (SeatFilled)`}</code>
                <span>Navigate</span><span>{adminSlot !== 1 ? <button type="button" className="button ghost small" onClick={() => { setAdminSlot(1); setAdminCycle(null); }}>Back to slot 1</button> : 'Slot 1 (entry slot)'}</span>
              </div>
            ) : null}

            {adminAvailableCycles.length > 0 && !adminIsProjected ? (
              <div className="admin-cycle-picker">
                {adminAvailableCycles.map((cycleNumber) => (
                  <button
                    key={cycleNumber}
                    type="button"
                    className={`admin-slot-chip active${adminSelectedCycleNumber === cycleNumber ? ' selected' : ''}`}
                    onClick={() => setAdminCycle(cycleNumber)}
                    title={`Cycle ${cycleNumber}`}
                  >
                    <strong>{cycleNumber}</strong>
                  </button>
                ))}
              </div>
            ) : null}

            {adminDisplayCycle ? (
              <>
                <div className="matrix-panel-heading"><div><span className="eyebrow">{adminIsProjected && adminProjectedSlot?.source !== 'onchain' ? 'TOPOLOGY · NOT CONFIRMED ON-CHAIN' : 'ON-CHAIN TOPOLOGY'}</span><h3>Slot {slotLabel(adminDisplayCycle.slotNumber)}{adminProjectedSlot?.virtual ? ' · virtual' : ''} · Cycle {adminDisplayCycle.cycleNumber}</h3></div><StatusPill tone={adminIsProjected ? 'violet' : adminDisplayCycle.status === 'completed' ? 'green' : 'violet'}>{adminIsProjected ? (adminProjectedSlot?.source === 'onchain' ? 'On-chain' : 'Not confirmed') : adminDisplayCycle.status}</StatusPill></div>
                {adminIsProjected ? <p className="empty-copy">{adminProjectedSlot?.source === 'onchain' ? 'Read from the contract: every seat is an indexed SeatFilled event; all twelve positions of every cycle this slot has reached, filled or open.' : 'This slot view is not marked as the contract\'s indexed SeatFilled events; its seats are not confirmed on-chain.'}</p> : null}
                {adminIsProjected && adminProjectedSlot ? (
                  <div className="lookup-position-grid">
                    <ProjectedSlotPanel slot={adminProjectedSlot} onChain={adminProjectedSlot.source === 'onchain' || explorerResult.explorer.slots.some((s) => s.slotNumber === adminProjectedSlot.slotNumber && s.status !== 'locked')} selectedCycle={adminSelectedCycleNumber} onSelectCycle={setAdminCycle} columns={6} />
                  </div>
                ) : null}
                <MatrixGraph nodes={adminMatrixNodes} rootId={explorerResult.member.bdId} cycle={adminDisplayCycle.cycleNumber} />
                <div className="cycle-head"><strong>Cycle {adminDisplayCycle.cycleNumber}</strong><span>{adminDisplayCycle.completedPositions}/{adminDisplayCycle.totalPositions}</span><StatusPill tone={adminDisplayCycle.status === 'completed' ? 'green' : 'violet'}>{adminDisplayCycle.status}</StatusPill></div>
                {adminDisplayCycle.placements.length > 0 ? (
                  <>
                    <div className="ledger-head"><span>Pos</span><span>Member</span><span>Type</span><span>Amount (atomic)</span><span>Tx hash</span></div>
                    {adminDisplayCycle.placements.map((placement) => (
                      <div className="ledger-row" key={`${adminDisplayCycle.slotNumber}-${adminDisplayCycle.cycleNumber}-${placement.positionNumber}`}>
                        <span>{placement.positionNumber}</span>
                        <code>{placement.memberBdId}</code>
                        <span>{placement.placementType}</span>
                        <code>{placement.amountAtomic}</code>
                        <code>{placement.txHash ?? '—'}</code>
                      </div>
                    ))}
                  </>
                ) : <p className="empty-copy">No placements recorded for this cycle.</p>}
              </>
            ) : <p className="empty-copy">No cycles have started for this member yet.</p>}

            <div className="panel-heading admin-subhead"><div><span className="eyebrow">ROUTING EVENTS (RULE 9 §42, ON-CHAIN)</span><h3>{routingEvents ? `${routingEvents.totalCount.toLocaleString()} event${routingEvents.totalCount === 1 ? '' : 's'} involving ${explorerResult.member.bdId}` : 'Loading...'}</h3></div><StatusPill tone="violet">{routingEvents?.uplineSearchLimit ? `On-chain limit ${routingEvents.uplineSearchLimit} seniors` : 'Contract Routed events'}</StatusPill></div>
            <p className="empty-copy">Every routing seat and every upgrade the Matrix contract executed is one Routed event: which ID was routed, from whose matrix and seat, the candidates it checked in order (RoutingCandidate events, ✓ = eligible), who was selected, and the exact seat it took. Newest first. Hover the count for the candidate path.</p>
            <div className="admin-scroll">
              <div className="ledger-head"><span>#</span><span>Routed ID · from</span><span>Checked</span><span>Selected</span><span>Seat taken</span></div>
              {(routingEvents?.items ?? []).map((row) => (
                <div className="ledger-row" key={row.routingEventId}>
                  <span data-label="#"><code>#{row.routingEventId}</code> <small>{ROUTING_TYPE_LABEL[row.routingType] ?? row.routingType}{row.routingDepth > 0 ? ` · depth ${row.routingDepth}` : ''}{row.at ? ` · ${formatDate(row.at)}` : ''}</small></span>
                  <span data-label="Routed ID · from"><code>{row.sourceId}</code> from <code>{row.slotOwnerId}</code> S{slotLabel(row.sourceSlotNumber)} C{row.sourceCycleNumber} P{row.sourcePosition}</span>
                  <span data-label="Checked" title={row.candidatePath.length > 0 ? (row.candidates ?? []).map((candidate) => `${candidate.bdId}${candidate.eligible ? ' ✓' : ''}`).join(' → ') || row.candidatePath.join(' → ') : 'No candidate available'}>{row.candidateEvaluationCount} checked{row.candidatePath.length > 0 ? ` (${row.candidatePath.slice(0, 3).join(', ')}${row.candidatePath.length > 3 ? ', …' : ''})` : ''}</span>
                  <strong data-label="Selected">{row.selectedCandidateId ?? 'Treasury'}</strong>
                  <span data-label="Seat taken">{row.finalDestinationType === 'placement' ? `S${slotLabel(row.destinationSlotNumber)} C${row.destinationCycleNumber ?? '?'} P${row.destinationPosition ?? '?'}` : 'Treasury pool'}{row.txHash ? <> <a href={`${EXPLORER_URL}/tx/${row.txHash}`} target="_blank" rel="noreferrer" title={row.txHash}><ExternalLink size={11} /></a></> : null}</span>
                </div>
              ))}
              {routingEvents && routingEvents.items.length === 0 ? <p className="empty-copy">No routing event involves this ID yet.</p> : null}
            </div>

            <div className="panel-heading admin-subhead"><div><span className="eyebrow">RECENT TRANSACTIONS (RAW)</span><h3>{explorerResult.explorer.recentTransactions.length} record{explorerResult.explorer.recentTransactions.length === 1 ? '' : 's'}</h3></div></div>
            <div className="ledger-head"><span>Kind</span><span>Status</span><span>Amount (atomic)</span><span>Block</span><span>Tx hash</span></div>
            {explorerResult.explorer.recentTransactions.map((transaction) => (
              <div className="ledger-row" key={transaction.txHash}><span>{transaction.kind}</span><StatusPill tone={transaction.status === 'confirmed' ? 'green' : 'amber'}>{transaction.status}</StatusPill><code>{transaction.amountAtomic}</code><span>{transaction.blockNumber ?? 'pending'}</span><code>{transaction.txHash}</code></div>
            ))}

            {explorerResult.explorer.tokens.length > 0 ? (
              <>
                <div className="panel-heading admin-subhead"><div><span className="eyebrow">UTILITY TOKENS (RAW)</span><h3>{explorerResult.explorer.tokens.length}</h3></div></div>
                <div className="ledger-head"><span>Token</span><span>State</span><span>Provider</span><span>Created</span><span /></div>
                {explorerResult.explorer.tokens.map((token) => (
                  <div className="ledger-row" key={token.tokenCode}><code>{token.tokenCode}</code><span>{token.state}</span><span>{token.providerCode ?? '—'}</span><span>{token.createdAt}</span><span /></div>
                ))}
              </>
            ) : null}
          </>
        ) : null}
      </section>

      <section className="stats-grid four" id="admin-overview">
        <StatCard label="Registered IDs" value={overview ? overview.stats.totalMembers.toLocaleString() : '—'} detail={overview?.stats.placedMembers !== undefined ? `On-chain IdCreated · ${overview.stats.placedMembers.toLocaleString()} placed in the matrix (JoinPlaced)` : 'On-chain IdCreated'} icon={Users} tone="amber" />
        <StatCard label="RPC availability" value={`${healthyRpc} / ${rpc.length}`} detail="Latest provider probes" icon={Network} tone="cyan" delay={0.1} />
        <StatCard label="Finalized contract events" value={overview ? overview.stats.confirmedTransactions.toLocaleString() : '—'} detail={overview?.stats.lastEventAt ? `Indexed from the chain · last ${formatDate(overview.stats.lastEventAt)}` : 'Indexed from the chain'} icon={Activity} tone="violet" delay={0.15} />
        <StatCard label="Seats filled" value={overview?.stats.seatsFilled !== undefined ? overview.stats.seatsFilled.toLocaleString() : '—'} detail={overview ? `SeatFilled · ${overview.stats.completedCycles.toLocaleString()} cycles completed · ${(overview.stats.routingEvents ?? 0).toLocaleString()} routings (${(overview.stats.routingTreasuryFallbacks ?? 0).toLocaleString()} to Treasury) · paid to members ${formatToken(atomicToDecimal(overview.stats.distributedAtomic))}` : 'SeatFilled events'} icon={LayoutGrid} tone="green" delay={0.2} />
      </section>

      <section className="panel audit-table neon-panel" id="admin-test-register">
        <div className="panel-heading">
          <div><span className="eyebrow">TESTING TOOL · BSC TESTNET ONLY</span><h2>Register a test ID</h2></div>
          <button className="button ghost small" type="button" onClick={() => void loadTestRegistrations()}><RefreshCw size={14} /> Refresh</button>
        </div>
        <p className="empty-copy">
          Enter only the sponsor ID. A brand-new wallet is generated, funded with tBNB (gas) and mock BTCB (the entry amount) from the deployer wallet,
          then it approves and joins on-chain exactly like a wallet registration. The entry BTCB and all fees are deducted from the deployer wallet; the
          new address and the generated ID are shown below.{testRegs && !testRegs.enabled ? ' NOT CONFIGURED on this API (TEST_REGISTER_PRIVATE_KEY).' : ''}
        </p>
        <form className="admin-explorer-form" onSubmit={(event) => { event.preventDefault(); void runTestRegister(); }}>
          <label><Search size={14} /><input value={testSponsor} onChange={(event) => setTestSponsor(event.target.value)} placeholder="Sponsor ID, e.g. BD-1" spellCheck={false} /></label>
          <label title="CDT Benefit country (selectCountry is sent from the generated wallet after the join)"><input type="search" aria-label="Search test ID country" placeholder="Search: Dubai, India, US..." value={testCountrySearch} onChange={(event) => setTestCountrySearch(event.target.value)} style={{ marginRight: 6 }} /><select aria-label="Test ID country" value={testCountry} onChange={(event) => setTestCountry(event.target.value)}>{visibleTestCountryOptions.map((code) => <option key={code} value={code}>{countryLabel(code)}</option>)}</select></label>
          <button className="button primary small" type="submit" disabled={testBusy || (testRegs ? !testRegs.enabled : false)}>{testBusy ? 'Registering on-chain… (4 transactions)' : 'Register new ID'}</button>
        </form>
        <p className="empty-copy">No wallet connection is needed for this tool - the secret admin link is the credential. Each registration sends 4 transactions and takes about 20-40 seconds.{testRegs && !testRegs.enabled ? ' Button disabled: the API has no TEST_REGISTER_PRIVATE_KEY.' : ''}</p>
        {testError ? <p className="token-identity-error">{testError}</p> : null}
        {testDropped ? (
          <div className="transaction-callout warning" role="status" data-testid="test-register-dropped">
            <AlertTriangle />
            <div>
              <strong>The connection dropped while the registration was running — it usually completes on the server. Refreshing the registrations list…</strong>
              <p className="callout-note">
                {testDropped.checks === 0
                  ? 'The register is re-read automatically after 5 s and 20 s.'
                  : `Checked ${testDropped.checks} time${testDropped.checks === 1 ? '' : 's'} - no new ID listed yet.${testDropped.checks >= TEST_REGISTER_RECHECK_DELAYS_MS.length ? ' If it stays missing, read the API log before registering again: the deployer wallet may already have paid.' : ''}`}{' '}
                <button className="button ghost small" type="button" disabled={testDropped.checking} onClick={() => void recheckDroppedRegistration(testDropped.clickedAt, testDropped.knownIds)}>{testDropped.checking ? 'Refreshing…' : 'Refresh now'}</button>
              </p>
            </div>
          </div>
        ) : null}
        {testRecovered ? (
          <div className="transaction-callout success" role="status" data-testid="test-register-recovered">
            <CheckCircle2 />
            <div>
              <strong>Registration completed: {testRecovered.bdId} (country {testRecovered.country ? countryLabel(normalizeCode(testRecovered.country)) : 'not sent'})</strong>
              <p className="callout-note">
                Sponsor {testRecovered.sponsorBdId} · wallet <span style={{ wordBreak: 'break-all' }}>{testRecovered.wallet}</span> · entry {formatToken(atomicToDecimal(testRecovered.entryAmountAtomic))} ({testRecovered.btcbSource}) · join tx <a href={`${EXPLORER_URL}/tx/${testRecovered.joinTx}`} target="_blank" rel="noreferrer">{testRecovered.joinTx.slice(0, 12)}… <ExternalLink size={11} /></a> · {new Date(testRecovered.createdAt).toLocaleString()}.
                {' '}The wallet's private key is only returned in the original reply, which this browser did not receive{testRegs && (testRegs as { mainnetStaging?: boolean }).mainnetStaging ? ' - the API kept it in the encrypted key vault (tools/staging/export-test-wallets.mjs)' : ''}.
              </p>
            </div>
          </div>
        ) : null}
        {testResult ? (
          <div className="test-register-result">
            <dl className="primary-token-grid">
              <div><dt>New ID</dt><dd>{testResult.bdId}</dd></div>
              <div><dt>Sponsor</dt><dd>{testResult.sponsorBdId}</dd></div>
              <div><dt>New wallet</dt><dd style={{ wordBreak: 'break-all', fontSize: 12 }}>{testResult.wallet}</dd></div>
              <div><dt>Entry deducted</dt><dd>{formatToken(atomicToDecimal(testResult.entryAmountAtomic))} ({testResult.btcbSource})</dd></div>
              <div><dt>Gas funded</dt><dd>{(Number(testResult.gasWei) / 1e18).toFixed(4)} tBNB</dd></div>
              <div><dt>Deployer tBNB</dt><dd>{(Number(testResult.funder.bnbBeforeWei) / 1e18).toFixed(5)} → {(Number(testResult.funder.bnbAfterWei) / 1e18).toFixed(5)}</dd></div>
              <div><dt>Deployer BTCB</dt><dd>{formatToken(atomicToDecimal(testResult.funder.btcbBeforeAtomic))} → {formatToken(atomicToDecimal(testResult.funder.btcbAfterAtomic))}</dd></div>
              <div><dt>Join tx</dt><dd style={{ wordBreak: 'break-all', fontSize: 11 }}>{testResult.txs.join}</dd></div>
              <div><dt>Country</dt><dd>{testResult.country ?? testResult.countrySelection?.requested ?? testCountry}{(testResult.txs.selectCountry ?? testResult.countrySelection?.tx) ? <> · <a href={`${EXPLORER_URL}/tx/${testResult.txs.selectCountry ?? testResult.countrySelection?.tx}`} target="_blank" rel="noreferrer">selectCountry tx <ExternalLink size={11} /></a></> : testResult.countrySelection?.error ? <span className="token-identity-error"> · selectCountry failed: {testResult.countrySelection.error}</span> : ' · selectCountry not sent'}</dd></div>
            </dl>
            <p className="empty-copy">
              Wallet private key (testnet only, shown once - import it into MetaMask to log in as {testResult.bdId}):{' '}
              <button className="button ghost small" type="button" onClick={() => setShowTestKey((value) => !value)}>{showTestKey ? 'Hide' : 'Reveal'}</button>{' '}
              <button className="button ghost small" type="button" onClick={() => void navigator.clipboard?.writeText(testResult.privateKey)}>Copy</button>
              {showTestKey ? <code style={{ display: 'block', wordBreak: 'break-all', marginTop: 6 }}>{testResult.privateKey}</code> : null}
            </p>
          </div>
        ) : null}
        {testRegs && testRegs.items.length ? (
          <div className="pt-table-wrap">
            <table className="pt-table">
              <thead><tr><th>ID</th><th>Sponsor</th><th>Wallet</th><th>Country</th><th>Entry</th><th>BTCB from</th><th>Join tx</th><th>When</th></tr></thead>
              <tbody>
                {testRegs.items.map((item) => (
                  <tr key={item.id}>
                    <td><strong>{item.bdId}</strong></td>
                    <td>{item.sponsorBdId}</td>
                    <td>{shortenAddress(item.wallet, 6)}</td>
                    <td>{item.country ? countryLabel(normalizeCode(item.country)) : '—'}</td>
                    <td>{formatToken(atomicToDecimal(item.entryAmountAtomic))}</td>
                    <td>{item.btcbSource}</td>
                    <td>{item.joinTx.slice(0, 12)}…</td>
                    <td>{new Date(item.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
      <AdminSlotTestTool slug={slug} />

      <AdminEraTestTool slug={slug} />

      <section className="panel audit-table" id="admin-registrations">
        <div className="panel-heading"><div><span className="eyebrow">REGISTERED IDS</span><h2>Registrations</h2></div><StatusPill tone="violet">{registrationsTotal.toLocaleString()} total</StatusPill></div>
        <form className="admin-explorer-form" onSubmit={submitRegistrationFilter}>
          <label><CalendarClock size={14} /><input type="datetime-local" value={registrationsFrom} onChange={(event) => setRegistrationsFrom(event.target.value)} aria-label="Registered from" /></label>
          <label><CalendarClock size={14} /><input type="datetime-local" value={registrationsTo} onChange={(event) => setRegistrationsTo(event.target.value)} aria-label="Registered to" /></label>
          <button className="button secondary small" type="submit" disabled={registrationsBusy}>{registrationsBusy ? 'Filtering...' : 'Apply filter'}</button>
          <button className="button ghost small" type="button" onClick={clearRegistrationFilter} disabled={registrationsBusy || (!registrationsFrom && !registrationsTo)}>Clear</button>
        </form>
        {registrationsError ? <p className="empty-copy">{registrationsError}</p> : null}
        <div className="admin-scroll">
          <div className="ledger-head registrations-head"><span>ID</span><span>Sponsor</span><span>Wallet</span><span>Matrix (on-chain)</span><span>Country</span><span>Registered</span></div>
          {registrations.map((row) => (
            <div className="ledger-row registrations-row" key={row.bdId}>
              <code>{row.bdId}</code>
              <code>{row.sponsorBdId ?? 'Root'}</code>
              <span>{shortenAddress(row.walletAddress, 6)}</span>
              <span title={row.placedAt ? `JoinPlaced ${formatDate(row.placedAt)}` : undefined}>
                <StatusPill tone={row.placed ? 'green' : row.placed === false ? 'amber' : 'neutral'}>{row.placed ? 'placed' : row.placed === false ? 'not placed' : 'no JoinPlaced'}</StatusPill>
                {row.highestSlot ? <small> Slot {row.highestSlotLabel ?? slotLabel(row.highestSlot)} · {(row.directCount ?? 0).toLocaleString()} placed direct{row.directCount === 1 ? '' : 's'}</small> : null}
                {row.era && row.era !== 'standard' ? <small> · {row.era} era</small> : null}
              </span>
              <span title="CDT Benefit country (CountrySelected)">{row.country === undefined ? '—' : normalizeCode(row.country) || <StatusPill tone="amber">not selected</StatusPill>}</span>
              <span>{formatDate(row.joinedAt)}{row.joinTxHash ? <> <a href={`${EXPLORER_URL}/tx/${row.joinTxHash}`} target="_blank" rel="noreferrer" title={row.joinTxHash}><ExternalLink size={11} /></a></> : null}</span>
            </div>
          ))}
          {registrations.length === 0 && !registrationsBusy ? <p className="empty-copy">No registrations match this filter.</p> : null}
        </div>
      </section>

      <section className="panel audit-table neon-panel" id="admin-engine-settings">
        <div className="panel-heading"><div><span className="eyebrow">PLACEMENT ENGINE SETTING · ON-CHAIN (BDynamicMatrix)</span><h2>Senior sponsor check limit</h2></div><StatusPill tone="violet">Contract value {data.placementRules.uplineSearchLimit} levels</StatusPill></div>
        <p className="empty-copy">
          How many senior sponsors (levels up) the contract checks before it falls back to Treasury. Applied everywhere an upward search happens:
          Sponsor-1 routing (P1 of every cycle), Sponsor-2 routing (cycle-1 P6, cycle-2+ P3), and entry placement into a higher slot when the
          direct sponsor is not active there (rule 7). The downline search is separate: {data.placementRules.downlineBranches} branches x {data.placementRules.downlineDepth} generations = {data.placementRules.downlineCheckBudget} checks (rule 24, not lowered in testing).
          This is BDynamicMatrix.uplineSearchLimit itself (final 24, testing 6): saving sends setRoutingLimits from the connected matrix-owner wallet, and every panel follows the contract once the transaction is indexed. Nothing is stored on the server.
        </p>
        <form className="admin-explorer-form" onSubmit={(event) => { event.preventDefault(); void saveUplineLimit(); }}>
          <label><span className="browse-label">Levels up</span><input type="number" min={1} max={500} value={uplineLimitInput} onChange={(event) => setUplineLimitInput(event.target.value)} aria-label="Senior sponsor check limit" style={{ width: 90 }} /></label>
          <button className="button secondary small" type="submit" disabled={settingsBusy || !wallet.address || uplineLimitInput === String(data.placementRules.uplineSearchLimit)}>{settingsBusy ? 'Signing...' : 'Set on the contract'}</button>
          {!wallet.address ? <span className="empty-copy">Connect the governance (matrix owner) wallet to change this.</span> : null}
          {(data.placementRules as { source?: string }).source === 'default' ? <span className="empty-copy">Not indexed from the contract yet - showing the protocol default.</span> : null}
          {data.placementRules.updatedAt ? <span className="empty-copy">Last changed on-chain {formatDate(data.placementRules.updatedAt)}{(data.placementRules as { txHash?: string | null }).txHash ? <> · <a className="tx-link" href={`${EXPLORER_URL}/tx/${(data.placementRules as { txHash?: string | null }).txHash}`} target="_blank" rel="noreferrer">tx</a></> : null}</span> : null}
        </form>
        {settingsError ? <p className="empty-copy">{settingsError}</p> : null}
      </section>

      <section className="panel audit-table" id="admin-slot-report">
        <div className="panel-heading"><div><span className="eyebrow">SLOT-WISE ID COUNT · ON-CHAIN</span><h2>Slot distribution</h2></div><StatusPill tone="violet">{slotDistribution.reduce((sum, row) => sum + row.totalCount, 0).toLocaleString()} slot activations</StatusPill></div>
        <div className="admin-slot-picker">
          {slotDistribution.map((row) => (
            <button
              key={row.slotNumber}
              type="button"
              className={`admin-slot-chip${row.totalCount > 0 ? ' active' : ''}${row.activeCount === 0 && row.virtualCount > 0 ? ' virtual' : ''}${selectedReportSlot === row.slotNumber ? ' selected' : ''}`}
              onClick={() => void openSlotMembers(row.slotNumber)}
              title={`${slotTitle(row.slotNumber)}: ${row.activeCount} activated on-chain${row.virtualCount > 0 ? ` + ${row.virtualCount} virtual (seat lower-era IDs only)` : ''} · ${row.upgradedCount} upgraded to the next slot · ${row.upgradeWaitingCount} waiting for 2 directs · ${row.seatsFilled} seats filled · ${row.cyclesCompleted} cycles completed`}
            >
              <strong>{row.slotLabel ?? slotLabel(row.slotNumber)}</strong>
              <small>{row.activeCount}{row.virtualCount > 0 ? ` +${row.virtualCount}v` : ''}</small>
            </button>
          ))}
        </div>
        {selectedReportSlot !== null ? (
          <>
            {(() => {
              const row = slotDistribution.find((item) => item.slotNumber === selectedReportSlot);
              return (
                <div className="admin-kv" style={{ marginBottom: 14 }}>
                  <span>{slotTitle(selectedReportSlot)}</span><code>{slotMembersTotal.toLocaleString()} ID{slotMembersTotal === 1 ? '' : 's'} on-chain{row && row.virtualCount > 0 ? ` (${row.virtualCount} virtual)` : ''} · {(row?.seatsFilled ?? 0).toLocaleString()} seats filled · {(row?.cyclesCompleted ?? 0).toLocaleString()} cycles completed · {(row?.upgradedCount ?? 0).toLocaleString()} upgraded</code>
                  <span>Matrices</span><span><button type="button" className="button ghost small" onClick={() => browseOnlySlot(selectedReportSlot)}>Open {slotTitle(selectedReportSlot)} in the ID browser</button></span>
                </div>
              );
            })()}
            <div className="ledger-head"><span>ID</span><span>State</span><span>Activated</span><span>Cycle · next position</span><span>Upgrade / entry</span></div>
            {slotMembersBusy ? <p className="empty-copy">Loading...</p> : null}
            {slotMembers.map((row) => {
              const next = nextSlot(row.slotNumber);
              return (
                <div className="ledger-row" key={row.bdId}>
                  <code>{row.bdId}</code>
                  <span><StatusPill tone={row.virtual ? 'amber' : row.nextSlotOpened ? 'green' : 'violet'}>{row.virtual ? 'virtual' : row.nextSlotOpened ? 'upgraded' : 'active'}</StatusPill> <small>{row.directCount} placed direct{row.directCount === 1 ? '' : 's'} · {row.seatsFilled} seat{row.seatsFilled === 1 ? '' : 's'}</small></span>
                  <span>{row.activatedAt ? formatDate(row.activatedAt) : '—'}</span>
                  <span>Cycle {row.currentCycleNumber} · {row.currentOpenPosition > 12 ? 'cycle complete' : `P${row.currentOpenPosition}`}</span>
                  <span>
                    {row.virtual ? 'no upgrade (virtual)' : row.nextSlotOpened && next !== null ? `${slotTitle(next)} opened` : row.upgradeWaiting ? 'P4 filled - waiting for 2 placed directs' : next === null ? 'last slot' : 'not yet'}
                    {row.placedUnder ? <small> · entered under {row.placedUnder}{row.placedUnderLevelsUp ? ` (${row.placedUnderLevelsUp} up)` : ''}</small> : row.enteredViaTreasury ? <small> · entered via Treasury</small> : null}
                  </span>
                </div>
              );
            })}
            {!slotMembersBusy && slotMembers.length === 0 ? <p className="empty-copy">No ID holds this slot on-chain yet.</p> : null}
          </>
        ) : <p className="empty-copy">Each chip shows how many IDs hold the slot on-chain (indexed SlotOpened / JoinPlaced / VirtualSlotActivated; +Nv = virtual sub-slots). Click a slot to list its IDs with their live cycle pointer, or open it in the ID browser for the seats.</p>}
      </section>

      <section className="panel audit-table neon-panel" id="admin-id-browser">
        <div className="panel-heading"><div><span className="eyebrow">ID BROWSER</span><h2><LayoutGrid size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />Browse by slot, cycle &amp; position</h2></div><StatusPill tone="violet">{browseTotal.toLocaleString()} matching</StatusPill></div>
        <form className="browse-filters" onSubmit={submitBrowseFilter}>
          <SelectionPicker label="Slots" prefix="Slot " options={ALL_SLOT_KEYS} format={slotLabel} compare={compareSlots} selected={browseSlotsSel} onChange={setBrowseSlotsSel} range={browseRange.slots} onRange={(value) => setBrowseRange((previous) => ({ ...previous, slots: value }))} hint="Range, e.g. 2-5 or ½,1,3,7-9 (½ / ¼ = Half / Quarter sub-slots; empty = all)" />
          <SelectionPicker label="Cycles" prefix="Cycle " options={browseCycles.length > 0 ? browseCycles : [1]} selected={browseCyclesSel} onChange={setBrowseCyclesSel} range={browseRange.cycles} onRange={(value) => setBrowseRange((previous) => ({ ...previous, cycles: value }))} hint="Range, e.g. 1-3 (empty = every cycle)" />
          <SelectionPicker label="Positions" prefix="P" options={POSITION_NUMBERS} selected={browsePositionsSel} onChange={setBrowsePositionsSel} range={browseRange.positions} onRange={(value) => setBrowseRange((previous) => ({ ...previous, positions: value }))} hint="Range, e.g. P3-P7 or 1,6,8 (empty = all 12)" />
          <div className="browse-filter-row">
            <label className="browse-ids"><Search size={14} /><input value={browseIdsText} onChange={(event) => setBrowseIdsText(event.target.value)} placeholder="IDs: BD-5, BD-10-BD-20, 33 to 40 (empty = every ID)" aria-label="ID selection" spellCheck={false} /></label>
            <label><CalendarClock size={14} /><input type="datetime-local" value={browseFrom} onChange={(event) => setBrowseFrom(event.target.value)} aria-label="Joined from" /></label>
            <label><CalendarClock size={14} /><input type="datetime-local" value={browseTo} onChange={(event) => setBrowseTo(event.target.value)} aria-label="Joined to" /></label>
            <label>
              <span className="browse-label">Show</span>
              <Dropdown<number>
                ariaLabel="Page size"
                value={browsePageSize}
                onChange={setBrowsePageSize}
                options={[10, 25, 50, 100].map((size) => ({ value: size, label: `${size} IDs / page` }))}
              />
            </label>
            <button className="button secondary small" type="submit" disabled={browseBusy}>{browseBusy && browseItems.length === 0 ? 'Loading...' : 'Apply'}</button>
            <button className="button ghost small" type="button" onClick={clearBrowseFilters} disabled={browseBusy || !browseHasFilters}>Reset</button>
          </div>
        </form>
        <p className="empty-copy">Only slots the contract has activated are listed (indexed JoinPlaced / SlotOpened / VirtualSlotActivated). Every listed slot shows all 12 positions of every cycle it has reached, filled or open; each filled seat is a SeatFilled event the contract emitted (including seats placed with the testnet slot test tool).</p>
        <PositionLegend />
        {browseError ? <p className="empty-copy">{browseError}</p> : null}
        <div className="id-browser-grid wide">
          {browseItems.map((row) => {
            // Progression order: Slot ¼, Slot ½, then 1 .. 16.
            const openSlots = [...new Set(row.activatedSlots)].sort(compareSlots);
            const virtualSlots = new Set([...(row.virtualSlots ?? []), ...row.slots.filter((slot) => slot.virtual).map((slot) => slot.slotNumber)]);
            const chosen = cardSlot[row.bdId];
            const visibleSlots = chosen === undefined
              ? row.slots
              : [row.slots.find((slot) => slot.slotNumber === chosen) ?? cardExtraSlots[`${row.bdId}#${chosen}`]].filter((slot): slot is BrowseSlotView => Boolean(slot));
            return (
              <article className="id-browser-card neon-card" key={row.bdId}>
                <div className="id-browser-card-head">
                  <code>{row.bdId}</code>
                  <span className="id-browser-meta">Sponsor {row.sponsorBdId ?? 'root'} · Joined {formatDate(row.joinedAt)} · Placed directs {row.directCount.toLocaleString()}</span>
                </div>
                <div className="id-browser-slots">
                  <span className="browse-label">Slots open</span>
                  <div>
                    {openSlots.map((slotNumber) => {
                      const shown = visibleSlots.some((slot) => slot.slotNumber === slotNumber);
                      const isVirtual = virtualSlots.has(slotNumber);
                      const name = slotTitle(slotNumber, isVirtual);
                      return (
                        <button
                          key={slotNumber}
                          type="button"
                          className={`slot-open-chip${isVirtual ? ' virtual' : ''}${shown ? ' selected' : ''}`}
                          onClick={(event) => { if (event.shiftKey) browseOnlySlot(slotNumber); else void selectCardSlot(row.bdId, slotNumber, row); }}
                          title={`${name} is activated on-chain${isVirtual ? ' (virtual: seats lower-era IDs, no payment, no upgrade)' : ''}. Click to show this slot on this card only (Shift+click filters the whole list).`}
                        >
                          <CheckCircle2 size={11} />
                          Slot {slotLabel(slotNumber)}
                          {isVirtual ? <span className="slot-virtual-tag">virtual</span> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
                {visibleSlots.map((slot) => {
                  const key = `${row.bdId}#${slot.slotNumber}`;
                  const selectedCycle = cardCycles[key] ?? slot.cycles[slot.cycles.length - 1]?.cycleNumber ?? 1;
                  const gridSource = slotGridSource(slot, slot.onChain);
                  return (
                    <div className="id-browser-positions" key={key}>
                      <span className="browse-label" title={gridSource.title}>Slot {slot.slotLabel ?? slotLabel(slot.slotNumber)} <em className={gridSource.tone === 'onchain' ? 'onchain' : undefined}>{gridSource.short}</em></span>
                      <ProjectedSlotPanel compact slot={slot} onChain={slot.onChain} selectedCycle={selectedCycle} onSelectCycle={(cycle) => setCardCycles((previous) => ({ ...previous, [key]: cycle }))} positions={browsePositionsApplied} columns={4} showLegend={false} />
                    </div>
                  );
                })}
              </article>
            );
          })}
        </div>
        {!browseBusy && browseItems.length === 0 ? (
          <div className="slot-empty-state">
            <span className="slot-empty-icon"><CircleSlash2 size={22} /></span>
            <h3>No IDs match this selection</h3>
            <p>No ID holds the selected slots on-chain for the chosen cycles, positions, IDs or dates. The contract opens a slot for an ID once that ID's cycle-1 P4 of the previous slot fills and it has 2 placed directs.</p>
            <div className="slot-empty-actions">
              {browseSlotsSel.length > 0 ? <button className="button secondary small" type="button" onClick={clearBrowseFilters}>Show every slot</button> : null}
              {browseHasFilters ? <button className="button ghost small" type="button" onClick={clearBrowseFilters}>Clear filters</button> : null}
            </div>
          </div>
        ) : null}
        <p className="empty-copy">{browseItems.length.toLocaleString()} of {browseTotal.toLocaleString()} shown.</p>
        {browseItems.length < browseTotal ? (
          <div className="id-browser-load-more" ref={browseSentinelRef}>
            <button className="button ghost small" type="button" onClick={() => void loadBrowsePage(false)} disabled={browseBusy}>{browseBusy ? 'Loading more...' : 'Load more'}</button>
          </div>
        ) : null}
      </section>

      <section className="panel audit-table" id="admin-income-report">
        <div className="panel-heading"><div><span className="eyebrow">SPONSOR INCOME (ON-CHAIN)</span><h2>Income report</h2></div><StatusPill tone="violet">{incomeTotal.toLocaleString()} IDs · serial order</StatusPill></div>
        <p className="empty-copy">
          Every figure is a SponsorIncome event the Matrix contract emitted for an entry, split by the event&apos;s level (1 = direct sponsor, 2–{incomeMeta?.levelMaxDepth ?? 10} = level income). Amounts are what the contract paid
          (gross; Half / Quarter-era entries pay ½ / ¼); <em>net</em> is what the vault credited after its sponsor fee (RewardPaid). Listed in ID serial order (BD-1, BD-2, ...). Click a row to open its generations.
          {incomeMeta ? ` Level amounts configured on-chain (EconomicsConfigured, Standard era; reference only): ${levelAmountsText(incomeMeta.ratesByDepthAtomic, incomeMeta.levelMaxDepth)}.` : ''}
        </p>
        {incomeMeta?.totals ? (
          <p className="empty-copy">
            Paid to IDs: <strong>{formatToken(atomicToDecimal(incomeMeta.totals.paidAtomic))}</strong> in {incomeMeta.totals.paidCount.toLocaleString()} payment{incomeMeta.totals.paidCount === 1 ? '' : 's'} to {incomeMeta.totals.recipientCount.toLocaleString()} ID{incomeMeta.totals.recipientCount === 1 ? '' : 's'} (net {formatToken(atomicToDecimal(incomeMeta.totals.netAtomic))}, fee {formatToken(atomicToDecimal(incomeMeta.totals.feeAtomic))})
            {' · '}missed → Referral treasury: <strong>{formatToken(atomicToDecimal(incomeMeta.totals.missedAtomic))}</strong> ({incomeMeta.totals.missedCount.toLocaleString()})
            {' · '}all SponsorIncome events: {formatToken(atomicToDecimal(incomeMeta.totals.emittedAtomic))} ({incomeMeta.totals.emittedCount.toLocaleString()}).
          </p>
        ) : null}
        {incomeError ? <p className="empty-copy">{incomeError}</p> : null}
        <div className="income-groups">
          <div className="income-group-legend"><span>ID · joined</span><span>1st Gen (direct)</span><span>2nd Gen</span><span>3rd to {incomeMeta?.levelMaxDepth ?? 10}th Gen</span><span>Total received</span><span /></div>
          {incomeReport.map((row) => {
            const byDepth = new Map<number, string[]>();
            for (const item of row.levelIds) {
              const list = byDepth.get(item.depth) ?? [];
              list.push(item.bdId);
              byDepth.set(item.depth, list);
            }
            const chip = (id: string, depth: number, index: number) => (
              <button key={`${id}-${index}`} type="button" className={`generation-chip mini depth-${depth}`} onClick={() => { setExplorerInput(id); void runExplorerFor(id); }} title={`${id} · paid level ${depth} · open in the member explorer`}>{id}<i>G{depth}</i></button>
            );
            const payments = (count: number) => `${count} payment${count === 1 ? '' : 's'}`;
            return (
              <details className="income-group" key={row.bdId}>
                <summary>
                  <span className="income-group-id"><code>{row.bdId}</code><small>{formatDate(row.joinedAt)}</small></span>
                  <span className="income-group-stat gen-direct"><strong>{row.directPartners}</strong> {row.directPartners === 1 ? 'payment' : 'payments'} = <b>{formatToken(atomicToDecimal(row.directTotalAtomic))}</b></span>
                  <span className="income-group-stat gen-second" data-label="2nd Gen"><strong>{row.secondGenCount}</strong> {row.secondGenCount === 1 ? 'payment' : 'payments'} = <b>{formatToken(atomicToDecimal(row.secondGenTotalAtomic))}</b></span>
                  <span className="income-group-stat gen-level" data-label={`3rd to ${incomeMeta?.levelMaxDepth ?? 10}th Gen`}><strong>{row.deepGenCount}</strong> {row.deepGenCount === 1 ? 'payment' : 'payments'} = <b>{formatToken(atomicToDecimal(row.deepGenTotalAtomic))}</b></span>
                  <span className="income-group-total" title={`Net after the vault's sponsor fee: ${formatToken(atomicToDecimal(row.netReceivedAtomic))}`}>{formatToken(atomicToDecimal(row.receivedTotalAtomic))}</span>
                  <ChevronDown size={15} className="income-group-caret" />
                </summary>
                <div className="income-group-body">
                  <p className="empty-copy">Received {formatToken(atomicToDecimal(row.receivedTotalAtomic))} gross · net credited {formatToken(atomicToDecimal(row.netReceivedAtomic))} · sponsor fee {formatToken(atomicToDecimal(row.feeAtomic))}{row.lastPaidAt ? ` · last paid ${formatDate(row.lastPaidAt)}` : ''}</p>
                  <details className="income-gen depth-1" open>
                    <summary><span className="generation-badge">G1</span><span>Level 1 · direct sponsor income</span><em>{payments(row.directPartners)} = {formatToken(atomicToDecimal(row.directTotalAtomic))}</em></summary>
                    <div className="income-chips gen-direct">
                      {row.directIds.map((id, index) => chip(id, 1, index))}
                      {row.directIdsTruncated ? <em>+{row.directPartners - row.directIds.length} more</em> : null}
                      {row.directIds.length === 0 ? <em>none yet</em> : null}
                    </div>
                  </details>
                  {(row.byLevel ?? []).filter((level) => level.level >= 2).map((level) => {
                    const ids = byDepth.get(level.level) ?? [];
                    return (
                      <details className={`income-gen depth-${level.level}`} key={level.level}>
                        <summary><span className="generation-badge">G{level.level}</span><span>Level {level.level} · level income</span><em>{payments(level.count)} = {formatToken(atomicToDecimal(level.amountAtomic))}</em></summary>
                        <div className="income-chips gen-level">
                          {ids.map((id, index) => chip(id, level.level, index))}
                          {ids.length === 0 ? <em>none yet</em> : null}
                        </div>
                      </details>
                    );
                  })}
                  {row.levelIdsTruncated ? <p className="empty-copy">Showing the first {row.levelIds.length} of {row.levelTwoToSixPartners} level payers. Open the per-payment breakdown for the complete list.</p> : null}
                  <div><button className="button ghost small" type="button" onClick={() => void openIncomeBreakdown(row.bdId)}>Per-payment breakdown</button></div>
                </div>
              </details>
            );
          })}
          {incomeReport.length === 0 && !incomeBusy ? <p className="empty-copy">No ID is indexed yet.</p> : null}
        </div>
        {selectedIncomeId ? (
          <div className="income-breakdown">
            <div className="panel-heading admin-subhead">
              <div><span className="eyebrow">SPONSOR INCOME · EVERY PAYMENT (ON-CHAIN)</span><h3>{selectedIncomeId}</h3></div>
              <button className="button ghost small" type="button" onClick={() => { setSelectedIncomeId(null); setIncomeBreakdown(null); }}>Close</button>
            </div>
            {incomeBreakdownBusy ? <p className="empty-copy">Loading...</p> : null}
            {incomeBreakdown ? (
              <>
                <div className="ledger-head"><span>Paid by (entry)</span><span>Level</span><span>Paid at</span><span>Amount (net)</span><span>Tx</span></div>
                {incomeBreakdown.contributors.map((row) => (
                  <div className="ledger-row" key={`${row.txHash}-${row.bdId}-${row.depth}`}>
                    <code data-label="Paid by">{row.bdId}</code>
                    <span data-label="Level">{row.depth === 1 ? 'Level 1 · direct sponsor' : `Level ${row.depth}`}</span>
                    <span data-label="Paid at" title={`${row.bdId} registered ${formatDate(row.joinedAt)}`}>{formatDate(row.paidAt)}</span>
                    <strong data-label="Amount">{formatToken(atomicToDecimal(row.amountAtomic))}{row.netAtomic !== null ? <small> (net {formatToken(atomicToDecimal(row.netAtomic))})</small> : null}</strong>
                    <span data-label="Tx"><a href={`${EXPLORER_URL}/tx/${row.txHash}`} target="_blank" rel="noreferrer" title={row.txHash}>{row.txHash.slice(0, 10)}… <ExternalLink size={11} /></a></span>
                  </div>
                ))}
                {incomeBreakdown.contributors.length === 0 ? <p className="empty-copy">No SponsorIncome event has paid this ID yet.</p> : null}
                <div className="route-total"><CheckCircle2 size={15} /><span>Received on-chain (gross · net)</span><strong>{formatToken(atomicToDecimal(incomeBreakdown.receivedTotalAtomic))} · {formatToken(atomicToDecimal(incomeBreakdown.netReceivedAtomic))}</strong></div>
              </>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="panel audit-table" id="admin-missed-income">
        <div className="panel-heading">
          <div><span className="eyebrow">MISSED SPONSOR INCOME (ON-CHAIN)</span><h2>Income with no eligible recipient</h2></div>
          <StatusPill tone="amber">{missed ? `${missed.sourceCount.toLocaleString()} source ID${missed.sourceCount === 1 ? '' : 's'}` : '—'}</StatusPill>
        </div>
        <p className="empty-copy">
          Every entry makes the contract walk ten sponsor levels (rule 12). Where a level has no placed upline (the chain reaches the root, or the upline is not placed in the matrix - the walk then stops) the contract emits SponsorIncome with recipient 0 and credits the amount to the REFERRAL_TREASURY bucket. Each row below is one such event; the total is cross-checked against the bucket&apos;s BucketCredited events. Click a box for the per-ID breakdown.
        </p>
        {missedError ? <p className="empty-copy">{missedError}</p> : null}
        {missed ? (
          <>
            <div className="missed-boxes">
              <button type="button" className={`missed-box${missedFilter === 'direct' ? ' selected' : ''}`} onClick={() => setMissedFilter(missedFilter === 'direct' ? null : 'direct')}>
                <span>Missed direct sponsor income (level 1)</span>
                <strong>{formatToken(atomicToDecimal(missed.missedDirectAtomic))}</strong>
                <small>{missed.missedDirectCount.toLocaleString()} SponsorIncome event{missed.missedDirectCount === 1 ? '' : 's'} with recipient 0</small>
              </button>
              <button type="button" className={`missed-box${missedFilter === 'level' ? ' selected' : ''}`} onClick={() => setMissedFilter(missedFilter === 'level' ? null : 'level')}>
                <span>Missed level income (2–{missed.levelMaxDepth ?? 10})</span>
                <strong>{formatToken(atomicToDecimal(missed.missedLevelAtomic))}</strong>
                <small>{missed.missedLevelCount.toLocaleString()} event{missed.missedLevelCount === 1 ? '' : 's'} · {(missed.byLevel ?? []).filter((level) => level.level >= 2 && level.count > 0).map((level) => `L${level.level} ${level.count}`).join(', ') || 'none'}</small>
              </button>
              <button type="button" className={`missed-box total${missedFilter === 'all' ? ' selected' : ''}`} onClick={() => setMissedFilter(missedFilter === 'all' ? null : 'all')}>
                <span>Total missed → Referral treasury</span>
                <strong>{formatToken(atomicToDecimal(missed.missedTotalAtomic))}</strong>
                <small>{(missed.missedDirectCount + missed.missedLevelCount).toLocaleString()} events from {missed.sourceCount.toLocaleString()} ID{missed.sourceCount === 1 ? '' : 's'}</small>
              </button>
            </div>
            {missed.referralTreasury ? (
              <div className="route-total">
                {missed.referralTreasury.matches ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
                <span>REFERRAL_TREASURY credits (BucketCredited, {missed.referralTreasury.creditCount.toLocaleString()}){missed.referralTreasury.matches ? ' = missed total' : ` differ by ${formatToken(atomicToDecimal(missed.referralTreasury.differenceAtomic))}`}</span>
                <strong>{formatToken(atomicToDecimal(missed.referralTreasury.creditedAtomic))}</strong>
              </div>
            ) : null}
          </>
        ) : missedBusy ? <p className="empty-copy">Loading...</p> : null}
        {missed && missedFilter ? (
          <div className="income-breakdown">
            <div className="panel-heading admin-subhead">
              <div><span className="eyebrow">MISSED INCOME BREAKDOWN · ON-CHAIN</span><h3>{missedFilter === 'direct' ? 'Level 1 (direct sponsor)' : missedFilter === 'level' ? `Levels 2–${missed.levelMaxDepth ?? 10}` : 'All levels'} · by source ID</h3></div>
              <button className="button ghost small" type="button" onClick={() => setMissedFilter(null)}>Close</button>
            </div>
            <div className="admin-scroll">
              <div className="ledger-head"><span>Sent by (entry)</span><span>Paid at</span><span>Level</span><span>Why nobody received it</span><span>Amount</span></div>
              {missed.items
                .filter((row) => missedFilter === 'all' || (missedFilter === 'direct' ? row.level === 1 : row.level >= 2))
                .map((row) => (
                  <div className="ledger-row" key={`${row.txHash}-${row.sourceBdId}-${row.level}`}>
                    <code data-label="Sent by">{row.sourceBdId}</code>
                    <span data-label="Paid at" title={`${row.sourceBdId} registered ${formatDate(row.sourceJoinedAt)}`}>{formatDate(row.at)} <a href={`${EXPLORER_URL}/tx/${row.txHash}`} target="_blank" rel="noreferrer" title={row.txHash}><ExternalLink size={11} /></a></span>
                    <span data-label="Level">{row.level === 1 ? 'Level 1 · direct sponsor' : `Level ${row.level}`}</span>
                    <span data-label="Why nobody received it">
                      {row.reason === 'no-upline'
                        ? <em className="missed-reason">No upline at this level</em>
                        : row.reason === 'chain-stopped'
                          ? <>{row.intendedRecipientBdId} <em className="missed-reason">(walk stopped at unplaced {row.stoppedAtBdId ?? 'upline'})</em></>
                          : <>{row.intendedRecipientBdId} <em className="missed-reason">(not placed in the matrix)</em></>}
                    </span>
                    <strong data-label="Amount">{formatToken(atomicToDecimal(row.amountAtomic))}</strong>
                  </div>
                ))}
            </div>
            <div className="route-total"><AlertTriangle size={15} /><span>Missed in this view</span><strong>{formatToken(atomicToDecimal(missedFilter === 'direct' ? missed.missedDirectAtomic : missedFilter === 'level' ? missed.missedLevelAtomic : missed.missedTotalAtomic))}</strong></div>
          </div>
        ) : null}
      </section>

      <AdminEngineSection slug={slug} />

      <section className="panel audit-table neon-panel" id="admin-primary-tokens">
        <div className="panel-heading">
          <div><span className="eyebrow">PRIMARY TOKENS (RULE 14)</span><h2>One B-Dynamic ID = one Primary Token (ID + T)</h2></div>
          <button className="button ghost small" type="button" onClick={() => void loadPrimaryTokens()}><RefreshCw size={14} /> Refresh</button>
        </div>
        {primaryTokensError ? <p className="empty-copy">{primaryTokensError}</p> : null}
        {primaryTokens ? (
          <>
            <p className="empty-copy">
              {primaryTokens.memberCount} IDs · {primaryTokens.mintedCount} tokens minted · identity mismatches: {primaryTokens.identityMismatchCount}
              {primaryTokens.duplicateIds.length ? ` · DUPLICATE tokens for ${primaryTokens.duplicateIds.join(', ')}` : ' · no duplicates'}.
              A token must carry exactly its ID's number (point 292); mismatches are flagged red. Every token figure is read from the
              indexed UtilityTokenIssued / state events of the token contract; an ID without a minted token shows none.
            </p>
            <TokenLiquiditySummary />
            <div className="pt-filter"><label><Search size={14} /><input placeholder="Filter by ID, token or status" value={primaryTokenFilter} onChange={(event) => { setPrimaryTokenFilter(event.target.value); setTokenPage(0); }} spellCheck={false} /></label></div>
            <Pager page={tokenPage} pageSize={25} total={primaryTokens.items.filter((item) => { const q = primaryTokenFilter.trim().toLowerCase(); return !q || item.bdId.toLowerCase().includes(q) || item.tokenLabel.toLowerCase().includes(q) || item.status.toLowerCase().includes(q); }).length} onPage={setTokenPage} />
            <div className="pt-table-wrap">
              <table className="pt-table">
                <thead><tr><th>Token</th><th>ID</th><th>Era</th><th>Original</th><th>Current</th><th>Burn %</th><th>Live value</th><th>Status</th><th>Unfreeze</th><th>Redemption</th><th>Identity</th></tr></thead>
                <tbody>
                  {primaryTokens.items
                    .filter((item) => { const q = primaryTokenFilter.trim().toLowerCase(); return !q || item.bdId.toLowerCase().includes(q) || item.tokenLabel.toLowerCase().includes(q) || item.status.toLowerCase().includes(q); })
                    .slice(tokenPage * 25, tokenPage * 25 + 25)
                    .map((item) => (
                      <tr key={item.bdId}>
                        <td><strong>{item.tokenLabel}</strong></td>
                        <td>{item.bdId}</td>
                        <td>{item.issueEra}</td>
                        <td>{item.minted ? item.originalRatio : '—'}</td>
                        <td>{item.burned || item.redeemed ? '—' : item.currentRatio ?? '—'}</td>
                        <td>{item.minted ? burnProgress(ratioToBps(item.originalRatio), ratioToBps(item.currentRatio), item.burned) : '—'}</td>
                        <td>{item.minted && !item.burned && !item.redeemed ? btcValue(tokenLiquidity?.valueFor(ratioToBps(item.currentRatio)) ?? null) : '—'}</td>
                        <td>{item.status}</td>
                        <td>{item.unfreezeAt ? new Date(item.unfreezeAt).toLocaleDateString() : '—'}</td>
                        <td>{item.redeemed ? 'Redeemed' : item.redemptionEligible ? 'Eligible' : '—'}</td>
                        <td style={{ color: item.identityValid ? '#43dea4' : '#ff8a8a', fontWeight: 700 }}>{item.identityValid ? 'OK' : `Mismatch #${item.tokenId}`}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </>
        ) : null}
      </section>
      <AdminTokenUnfreezeReport slug={slug} />

      <section className="panel audit-table neon-panel" id="admin-leaders-club">
        <div className="panel-heading">
          <div><span className="eyebrow">LEADERS CLUB (RULE 13, ON-CHAIN)</span><h2><Trophy size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />Four clubs · Club 4 · Club 8 · Club 12 · Club 16</h2></div>
          <button className="button ghost small" type="button" onClick={() => void loadLeadersClub()} disabled={leadersClubBusy}><RefreshCw size={14} /> {leadersClubBusy ? 'Reading…' : 'Refresh'}</button>
        </div>
        <p className="empty-copy">
          Every figure in this block is ON-CHAIN: the Leaders Club fund is the vault LEADERS_CLUB bucket (filled by every Cycle-2-onwards P7 seat), club membership is the
          contract's ClubEntered record (Club 4 on slot 4, Club 8 on slot 8, Club 12 on slot 12, Club 16 on slot 16 - lifetime), and settlements / per-ID payouts are the
          contract's ClubSettled / ClubPaid events. The fund splits 25% per club and each pool is divided equally among that club's eligible IDs (at least 2 directs).
          Settlements are run on-chain from the Contract V2 engine block "Leaders Club on-chain" (button "Settle on-chain now"). Fund custody (client decision, 21 Sep): a sub-ledger inside the
          Treasury smart contract - it can only move by this settlement rule; no wallet key can withdraw it.
        </p>
        {leadersClubError ? <p className="empty-copy">{leadersClubError}</p> : null}
        {leadersClub ? (
          <>
            {(() => {
              const chain = leadersClub.onChain;
              if (!chain) return <p className="empty-copy">This API build does not report the on-chain Leaders Club yet.</p>;
              const bscTx = (hash: string) => <a href={`${EXPLORER_URL}/tx/${hash}`} target="_blank" rel="noreferrer" className="tx-link" title={hash}>{hash.slice(0, 10)}…</a>;
              const nextIsPast = chain.nextSettlementAt !== null && Date.parse(chain.nextSettlementAt) <= Date.now();
              const chainPool = chain.pools.find((pool) => pool.club === openChainClub) ?? null;
              return (
                <>
                  {!chain.available ? <p className="empty-copy">No Leaders Club contract event has been indexed yet (no club entry, settlement or payout on-chain).</p> : null}
                  <div className="treasury-boxes">
                    <button type="button" className="treasury-box" onClick={() => setOpenChainClub(null)}>
                      <span>Leaders Club fund · vault (live)</span>
                      <strong>{clubVaultAtomic !== null ? formatToken(atomicToDecimal(clubVaultAtomic)) : '—'}</strong>
                      <small>
                        {clubVaultAtomic === null ? 'live vault balance unavailable (RPC) · ' : ''}
                        credited in total {formatToken(atomicToDecimal(chain.creditedAtomic))} ({chain.creditedCount.toLocaleString()} credit{chain.creditedCount === 1 ? '' : 's'})
                        {' · '}paid to IDs {formatToken(atomicToDecimal(chain.paidAtomic))} ({chain.payoutCount.toLocaleString()} payout{chain.payoutCount === 1 ? '' : 's'})
                        {' · '}{chain.totalMembers.toLocaleString()} club member{chain.totalMembers === 1 ? '' : 's'}
                      </small>
                    </button>
                    <div className="treasury-box">
                      <span>Distributions (on-chain)</span>
                      <strong>{chain.settlementCount.toLocaleString()} settlement{chain.settlementCount === 1 ? '' : 's'}</strong>
                      <small>
                        Last {chain.lastSettledAt ? formatDate(chain.lastSettledAt) : 'none yet'}
                        {' · '}next {chain.nextSettlementAt ? `${formatDate(chain.nextSettlementAt)}${nextIsPast ? ' (due now)' : ''}` : 'not scheduled yet'}
                        {chain.periodSeconds ? ` · period ${Math.round(Number(chain.periodSeconds) / 86_400 * 100) / 100} day(s)` : ''}
                      </small>
                    </div>
                    {chain.pools.map((pool) => (
                      <button key={`chain-${pool.club}`} type="button" className={`treasury-box${openChainClub === pool.club ? ' selected' : ''}`} onClick={() => setOpenChainClub(openChainClub === pool.club ? null : pool.club)}>
                        <span>{pool.label} · slot {pool.club}{pool.lifetime ? ' · lifetime' : ''}</span>
                        <strong>{pool.memberCount.toLocaleString()} member{pool.memberCount === 1 ? '' : 's'}</strong>
                        <small>{pool.eligibleCount.toLocaleString()} eligible (2+ directs) · paid {formatToken(atomicToDecimal(pool.paidAtomic))}{BigInt(pool.carriedOutAtomic) > 0n ? ` · carried ${formatToken(atomicToDecimal(pool.carriedOutAtomic))}` : ''} · click for the member list</small>
                      </button>
                    ))}
                  </div>
                  {chainPool ? (
                    <div className="income-breakdown treasury-detail" id="leaders-club-onchain-detail">
                      <div className="panel-heading admin-subhead">
                        <div><span className="eyebrow">CLUB MEMBERS · ON-CHAIN</span><h3>{chainPool.label} — entry slot {chainPool.club}{chainPool.lifetime ? ' (lifetime)' : ''}</h3></div>
                        <button className="button ghost small" type="button" onClick={() => setOpenChainClub(null)}>Close</button>
                      </div>
                      <div className="admin-scroll">
                        <div className="ledger-head"><span>ID</span><span>Entered club</span><span>Status</span><span>Directs</span><span>Club income received</span></div>
                        {chainPool.members.map((member) => (
                          <div className="ledger-row" key={`chain-${member.bdId}`}>
                            <code data-label="ID">{member.bdId}</code>
                            <span data-label="Entered club">{formatDate(member.enteredAt)}</span>
                            <span data-label="Status">{member.eligible ? 'eligible' : 'not eligible yet'}</span>
                            <span data-label="Directs">{member.directCount} of 2</span>
                            <strong data-label="Club income received" className="income-total">{formatToken(atomicToDecimal(member.receivedAtomic))}</strong>
                          </div>
                        ))}
                        {chainPool.members.length === 0 ? <p className="empty-copy">No ID is in {chainPool.label} on-chain yet.</p> : null}
                      </div>
                    </div>
                  ) : null}
                  <div className="panel-heading admin-subhead"><div><span className="eyebrow">DISTRIBUTION HISTORY · ON-CHAIN</span><h3>Club settlements and payouts</h3></div></div>
                  {chain.settlements.length === 0 ? <p className="empty-copy">No on-chain club settlement has run yet.</p> : null}
                  {chain.settlements.map((settlement) => (
                    <div key={`chain-settlement-${settlement.settlementId}`} className="admin-scroll">
                      <div className="cycle-head"><strong>#{settlement.settlementId} · {formatDate(settlement.settledAt)}</strong><span>fund {formatToken(atomicToDecimal(settlement.fundAtomic))} · {settlement.payouts.length.toLocaleString()} ID{settlement.payouts.length === 1 ? '' : 's'} paid · next {formatDate(settlement.nextSettlementAt)} · {bscTx(settlement.txHash)}</span><StatusPill tone="green">{formatToken(atomicToDecimal(settlement.payouts.reduce((sum, row) => sum + BigInt(row.amountAtomic), 0n).toString()))}</StatusPill></div>
                      <div className="ledger-head"><span>Club</span><span>Share + carried in</span><span>Eligible</span><span>Paid</span><span>Carried out</span></div>
                      {settlement.pools.map((pool) => (
                        <div className="ledger-row" key={`chain-pool-${settlement.settlementId}-${pool.club}`}>
                          <span data-label="Club">Club {pool.club}</span>
                          <span data-label="Share + carried in">{formatToken(atomicToDecimal(pool.shareAtomic))}{BigInt(pool.carriedInAtomic) > 0n ? ` + ${formatToken(atomicToDecimal(pool.carriedInAtomic))}` : ''}</span>
                          <span data-label="Eligible">{pool.eligibleCount.toLocaleString()}</span>
                          <strong data-label="Paid" className="income-total">{formatToken(atomicToDecimal(pool.paidAtomic))}</strong>
                          <span data-label="Carried out">{formatToken(atomicToDecimal(pool.carriedOutAtomic))}</span>
                        </div>
                      ))}
                      {settlement.payouts.length > 0 ? (
                        <>
                          <div className="ledger-head"><span>ID</span><span>Club</span><span>Paid at</span><span>Amount</span><span>Tx</span></div>
                          {settlement.payouts.map((payout) => (
                            <div className="ledger-row" key={`chain-payout-${settlement.settlementId}-${payout.club}-${payout.bdId}-${payout.txHash}`}>
                              <code data-label="ID">{payout.bdId}</code>
                              <span data-label="Club">Club {payout.club}</span>
                              <span data-label="Paid at">{formatDate(payout.at)}</span>
                              <strong data-label="Amount" className="income-total">{formatToken(atomicToDecimal(payout.amountAtomic))}</strong>
                              <span data-label="Tx">{bscTx(payout.txHash)}</span>
                            </div>
                          ))}
                        </>
                      ) : null}
                    </div>
                  ))}
                </>
              );
            })()}
            {/* Owner 2026-10-11: the period and the settlement are the CONTRACT's (BDynamicLeadersClub) - signed by a wallet, never a server setting. */}
            <form className="admin-explorer-form" onSubmit={(event) => { event.preventDefault(); void saveClubPeriod(); }}>
              <label><span className="browse-label">Contract period: every</span><input type="number" min={1} max={366} value={clubPeriodInput} onChange={(event) => setClubPeriodInput(event.target.value)} aria-label="Settlement period in days" style={{ width: 80 }} /><span className="browse-label">day(s)</span></label>
              <button className="button secondary small" type="submit" disabled={clubBusy || !wallet.address || clubPeriodInput === String(leadersClub.periodDays)}>{clubBusy ? 'Signing...' : 'Set period on the contract'}</button>
              <button className="button secondary small" type="button" onClick={() => void saveClubPeriod(true)} disabled={clubBusy || !wallet.address}>Calendar month (12 midnight IST)</button>
              <button className="button secondary small" type="button" onClick={() => void runClubSettlement()} disabled={clubBusy || !wallet.address}>Settle on-chain now</button>
              <span className="empty-copy">
                The period is BDynamicLeadersClub&apos;s own (setPeriod, contract owner only - it restarts the clock); settle() can be sent by any wallet once due and the server keeper sends it automatically. Settlements and payouts exist only as those transactions - there is no off-chain settlement.
                {leadersClub.nextSettlementAt ? ` Next due ${formatDate(leadersClub.nextSettlementAt)}.` : ' No settlement has run yet.'}
                {leadersClub.settlementDue ? ' Due now.' : ''}
              </span>
            </form>
            <details className="club-projection">
              <summary className="empty-copy" style={{ cursor: 'pointer' }}>
                FORECAST (NOT ON-CHAIN) - what the placement rules would credit; never a payment, never part of the on-chain figures above, not shown to members
              </summary>
            {/*
              * Owner 2026-10-11: only on-chain figures are shown as facts. The rules forecast stays here, collapsed and
              * separate, for cross-checking the contract against the rules: it is never added to any on-chain total. The
              * off-chain "settlements" and per-member "next settlement" shares were removed - they were calculations
              * shown like payouts.
              */}
            <div className="treasury-boxes">
              <div className="treasury-box projected" style={{ cursor: 'default' }}>
                <span>Forecast · fund the rules would credit</span>
                <strong>{formatToken(atomicToDecimal(leadersClub.fundAtomic))}</strong>
                <small>{leadersClub.seatCount.toLocaleString()} cycle-2+ P7 seat{leadersClub.seatCount === 1 ? '' : 's'} in the placement-rules replay · on-chain credited {leadersClub.onChain ? formatToken(atomicToDecimal(leadersClub.onChain.creditedAtomic)) : '—'} (see above) · a difference means the rules and the contract disagree</small>
              </div>
              {leadersClub.pools.map((pool) => (
                <div key={pool.club} className="treasury-box projected" style={{ cursor: 'default' }}>
                  <span>Forecast · {pool.label}</span>
                  <strong>{formatToken(atomicToDecimal(pool.shareAtomic))}</strong>
                  <small>{pool.sharePercent}% of the forecast fund · {pool.memberCount.toLocaleString()} ID{pool.memberCount === 1 ? '' : 's'} with slot {pool.club} activated on-chain and 2 directs · not a payment</small>
                </div>
              ))}
            </div>
            </details>
          </>
        ) : leadersClubBusy ? <p className="empty-copy">Loading…</p> : null}
      </section>

      <AdminCdtBenefitSection slug={slug} />

      <section className="panel audit-table" id="admin-treasury">
        <div className="panel-heading">
          <div><span className="eyebrow">TREASURY VAULT</span><h2><Landmark size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />Company treasury</h2></div>
          <button className="button ghost small" type="button" onClick={() => void loadTreasury()} disabled={treasuryBusy}><RefreshCw size={14} /> {treasuryBusy ? 'Reading…' : 'Refresh'}</button>
        </div>
        {treasury?.vaultAddress ? (
          <div className="treasury-address">
            <span>Vault contract</span>
            <a href={`${EXPLORER_URL}/address/${treasury.vaultAddress}`} target="_blank" rel="noreferrer"><code>{treasury.vaultAddress}</code> <ExternalLink size={12} /></a>
            {treasury.onChain ? <small>Owned by governance <code>{shortenAddress(treasury.onChain.ownerAddress)}</code> · read at block {Number(treasury.onChain.blockNumber).toLocaleString()}</small> : null}
          </div>
        ) : null}
        <p className="empty-copy">
          Every entry payment is accounted into this contract on-chain; no single key can withdraw from it. The balance below is read live from the contract; the ledger is the finalized event history the indexer holds. Every figure in this section is read from the contract or its indexed events.
        </p>
        <CdtSettledLine />
        {treasuryError ? <p className="empty-copy">{treasuryError}</p> : null}
        {treasury?.onChainError ? <p className="empty-copy">Live read unavailable: {treasury.onChainError}. Showing indexed history only.</p> : null}
        <p className="empty-copy">
          Pool tiles show only the live vault bucket balance read from the contract (the same numbers as "Vault buckets (live)" in the Contract V2 engine section). Click a tile for that bucket&apos;s on-chain account: every credit / movement the contracts emitted (BucketCredited, UpgradeHeld, UpgradeReleased, EscrowParked / Reclaimed / Forfeited, SponsorIncome to nobody), each paired with the seat that produced it. Payouts, pool fees and governance withdrawals lower the live balance, so a bucket&apos;s credits are not its balance.
        </p>
        {treasury ? (
          <div className="treasury-boxes">
            <button type="button" className={`treasury-box${treasuryView === 'ledger' ? ' selected' : ''}`} onClick={() => setTreasuryView(treasuryView === 'ledger' ? null : 'ledger')}>
              <span>On-chain balance</span>
              <strong>{treasury.onChain ? formatToken(atomicToDecimal(treasury.onChain.balanceAtomic)) : '—'}</strong>
              <small>{treasury.onChain ? `Accounted ${formatToken(atomicToDecimal(treasury.onChain.totalAccountedAtomic))} · click for how it accumulated` : 'live read unavailable'}</small>
            </button>
            <button type="button" className={`treasury-box${treasuryView === 'ledger' ? ' selected' : ''}`} onClick={() => setTreasuryView(treasuryView === 'ledger' ? null : 'ledger')}>
              <span>Indexed history</span>
              <strong>{formatToken(atomicToDecimal(treasury.indexed.netAtomic))}</strong>
              <small>{treasury.indexed.inboundCount.toLocaleString()} inbound · {(treasury.indexed.withdrawnCount ?? 0).toLocaleString()} withdrawals ({formatToken(atomicToDecimal(treasury.indexed.withdrawnAtomic ?? '0'))}) · {treasury.indexed.settledCount.toLocaleString()} settled · {treasury.indexed.allocationCount.toLocaleString()} allocations in {treasury.buckets.length} bucket{treasury.buckets.length === 1 ? '' : 's'}{treasury.indexed.lastEventAt ? ` · last ${formatDate(treasury.indexed.lastEventAt)}` : ''}</small>
            </button>
            {TREASURY_POOL_TILES.map((tile) => {
              const account = tile.accountKey ? treasury.accounts.find((item) => item.key === tile.accountKey) ?? null : null;
              const live = vaultBuckets?.[tile.bucket];
              const headline = live !== undefined ? formatToken(atomicToDecimal(live)) : vaultBuckets === null && !vaultBucketsError ? 'Reading…' : '—';
              const body = (
                <>
                  <span>{tile.label}</span>
                  <strong>{headline}</strong>
                  <small>On-chain bucket {tile.bucket} · live balance{vaultBucketsError ? ` · read unavailable: ${vaultBucketsError}` : ''}{account ? ` · click for its ${account.count.toLocaleString()} on-chain event${account.count === 1 ? '' : 's'}` : ''}</small>
                </>
              );
              return account ? (
                <button key={tile.bucket} type="button" className={`treasury-box${treasuryView === account.key ? ' selected' : ''}`} onClick={() => setTreasuryView(treasuryView === account.key ? null : account.key)}>{body}</button>
              ) : (
                <div key={tile.bucket} className="treasury-box" style={{ cursor: 'default' }}>{body}</div>
              );
            })}
            {treasury.accounts.filter((account) => !TREASURY_TILE_ACCOUNT_KEYS.has(account.key)).map((account) => (
              <button key={account.key} type="button" className={`treasury-box${treasuryView === account.key ? ' selected' : ''}`} onClick={() => setTreasuryView(treasuryView === account.key ? null : account.key)}>
                <span>{account.label}</span>
                <strong>{formatToken(atomicToDecimal(account.outAtomic !== '0' && account.inAtomic === '0' ? account.outAtomic : account.amountAtomic))}</strong>
                <small>{account.count.toLocaleString()} on-chain event{account.count === 1 ? '' : 's'} · click for ID-wise amounts</small>
              </button>
            ))}
          </div>
        ) : treasuryBusy ? <p className="empty-copy">Loading…</p> : null}
        {treasury && treasuryView && treasuryView !== 'ledger' ? (() => {
          const account = treasury.accounts.find((item) => item.key === treasuryView);
          if (!account) return null;
          return (
            <div className="income-breakdown treasury-detail" id="treasury-detail">
              <div className="panel-heading admin-subhead">
                <div><span className="eyebrow">ON-CHAIN ACCOUNT</span><h3>{account.label}</h3></div>
                <button className="button ghost small" type="button" onClick={() => setTreasuryView(null)}>Close</button>
              </div>
              <p className="empty-copy">{account.description} Every seat-linked row can be checked against the ID browser above (owner ID, slot, cycle, position) and on BscScan.</p>
              {(() => {
                const tile = TREASURY_POOL_TILES.find((item) => item.accountKey === account.key);
                const live = tile ? vaultBuckets?.[tile.bucket] : undefined;
                if (!tile) return null;
                return (
                  <p className="empty-copy">
                    Live on-chain bucket {tile.bucket}: <strong>{live !== undefined ? formatToken(atomicToDecimal(live)) : vaultBucketsError ? `unavailable (${vaultBucketsError})` : 'reading…'}</strong>
                    {account.bucketCreditedAtomic !== null ? <> · BucketCredited into it in total: <strong>{formatToken(atomicToDecimal(account.bucketCreditedAtomic))}</strong></> : null}
                    . Payouts, pool fees and governance withdrawals lower the live balance, so the events below add up to what came in, not to the balance.
                  </p>
                );
              })()}
              {account.key === 'token_liquidity' ? <TokenLiquiditySummary /> : null}
              {account.key === 'missed_referral' ? (
                <p className="empty-copy">{account.count.toLocaleString()} SponsorIncome events with recipient 0 = {formatToken(atomicToDecimal(account.amountAtomic))}; the REFERRAL_TREASURY bucket was credited {formatToken(atomicToDecimal(account.bucketCreditedAtomic ?? '0'))}. The per-ID list is in the Missed sponsor income section above.</p>
              ) : (
                <div className="admin-scroll">
                  <div className="ledger-head"><span>Seat (owner · slot · cycle · position)</span><span>Rule</span><span>ID</span><span>When</span><span>Amount</span></div>
                  {account.entries.map((row, index) => (
                    <div className="ledger-row" key={`${account.key}-${row.txHash}-${index}`}>
                      <span data-label="Seat">{row.ownerBdId ? <><code>{row.ownerBdId}</code> {row.cycleNumber === 0 ? `Slot ${row.slotLabel ?? row.slotNumber} entry` : `S${row.slotLabel ?? row.slotNumber} · C${row.cycleNumber} · P${row.position}`}</> : '—'}</span>
                      <span data-label="Rule">{ruleLabel(row.rule)}{row.detail ? <small className="treasury-row-detail">{row.detail}</small> : null}</span>
                      <strong data-label="ID">{row.occupantBdId}</strong>
                      <span data-label="When">{row.at ? formatDate(row.at) : '—'} <a href={`${EXPLORER_URL}/tx/${row.txHash}`} target="_blank" rel="noreferrer" title={row.txHash}><ExternalLink size={11} /></a></span>
                      <strong data-label="Amount" className={`income-total${row.direction === 'out' ? ' negative' : ''}`}>{row.direction === 'out' ? '−' : '+'}{formatToken(atomicToDecimal(row.amountAtomic))}</strong>
                    </div>
                  ))}
                  {account.entries.length === 0 ? <p className="empty-copy">No on-chain event in this account yet.</p> : null}
                  <div className="route-total"><CheckCircle2 size={15} /><span>Total · {account.count.toLocaleString()} event{account.count === 1 ? '' : 's'}{account.outAtomic !== '0' ? ` (in ${formatToken(atomicToDecimal(account.inAtomic))}, out ${formatToken(atomicToDecimal(account.outAtomic))})` : ''}</span><strong>{formatToken(atomicToDecimal(account.inAtomic === '0' && account.outAtomic !== '0' ? account.outAtomic : account.amountAtomic))}</strong></div>
                </div>
              )}
            </div>
          );
        })() : null}
        {treasury ? (
          <div className="income-breakdown payment-flow">
            <div className="panel-heading admin-subhead"><div><span className="eyebrow">100% PAYMENT FLOW CHECK · ON-CHAIN</span><h3>Every seat the contract filled, every slot, every cycle: where its payment went</h3></div><StatusPill tone="green">{treasury.paymentFlow.totalSeats.toLocaleString()} seats = 100%</StatusPill></div>
            <p className="empty-copy">Each SeatFilled event is counted once with the amount the contract attached to it, so the shares add to 100%. A routed seat whose search found nobody is counted once, as Treasury. A routing position (and a released upgrade) does not keep its payment: the same money moves on and fills another seat (or the slot-fallback treasury), where it is counted again. Those rows are marked "passed on" and are NOT extra fund - the real fund is the "final destination" total below.</p>
            <div className="ledger-head"><span>Destination</span><span>Seats</span><span>Share</span><span>Amount</span><span /></div>
            {treasury.paymentFlow.byDestination.map((row) => {
              const passedOn = row.passThrough ?? FLOW_PASS_THROUGH.has(row.kind);
              return (
                <div className={`ledger-row flow-${row.kind}`} key={row.kind}>
                  <span data-label="Destination">{row.label}{passedOn ? ' · passed on' : ''}</span>
                  <strong data-label="Seats">{row.count.toLocaleString()}</strong>
                  <span data-label="Share"><i className="flow-bar" style={{ width: `${Math.max(2, row.sharePercent)}%` }} /> {row.sharePercent.toFixed(2)}%</span>
                  <strong data-label="Amount" className="income-total">{row.count > 0 ? formatToken(atomicToDecimal(row.amountAtomic)) : '—'}{passedOn && row.count > 0 ? ' (passed on, not extra fund)' : ''}</strong>
                  <span />
                </div>
              );
            })}
            <div className="route-total"><CheckCircle2 size={15} /><span>Total</span><strong>{treasury.paymentFlow.totalSeats.toLocaleString()} seats · {treasury.paymentFlow.byDestination.reduce((sum, row) => sum + row.sharePercent, 0).toFixed(2)}% · {formatToken(atomicToDecimal(treasury.paymentFlow.byDestination.reduce((sum, row) => sum + BigInt(row.amountAtomic), 0n).toString()))} seat value</strong></div>
            <div className="route-total"><CheckCircle2 size={15} /><span>Real fund - final destinations (slot owners, upgrade holding, pools, treasuries, forfeited holdings)</span><strong>{formatToken(atomicToDecimal(treasury.paymentFlow.finalAtomic))}</strong></div>
            <div className="route-total"><CheckCircle2 size={15} /><span>Passed on to another seat (same money counted again there - not extra fund)</span><strong>{formatToken(atomicToDecimal(treasury.paymentFlow.passedOnAtomic))}</strong></div>
          </div>
        ) : null}
        {treasury && treasuryView === 'ledger' ? (
          <div className="income-breakdown treasury-detail" id="treasury-detail">
            <div className="panel-heading admin-subhead">
              <div><span className="eyebrow">HOW THE BALANCE ACCUMULATED</span><h3>Finalized vault events · entries in, withdrawals out</h3></div>
              <button className="button ghost small" type="button" onClick={() => setTreasuryView(null)}>Close</button>
            </div>
            {treasury.onChain && BigInt(treasury.onChain.balanceAtomic) !== BigInt(treasury.indexed.netAtomic) ? (
              <p className="empty-copy">Live balance {formatToken(atomicToDecimal(treasury.onChain.balanceAtomic))} differs from indexed net {formatToken(atomicToDecimal(treasury.indexed.netAtomic))} — a very recent transaction may not be finalized in the index yet (it clears within a minute).</p>
            ) : null}
            <div className="admin-scroll">
              <div className="ledger-head"><span>When</span><span>Event</span><span>From</span><span>Amount</span><span>Balance after</span></div>
              <Pager page={ledgerPage} pageSize={ledgerPageSize} total={treasury.ledger.length} onPage={setLedgerPage} onPageSize={setLedgerPageSize} />
              <p className="empty-copy"><button className="button ghost small" type="button" onClick={() => { setLedgerNewestFirst((value) => !value); setLedgerPage(0); }}>{ledgerNewestFirst ? 'Showing newest first - switch to oldest first' : 'Showing oldest first - switch to newest first'}</button> Entry received = a join's entry amount (the contract's current entry for its era) coming in · Member withdrawal / Liquidation / Owner withdrawal = BTCB leaving the vault · Internal allocation = money moved between two sub-accounts inside the vault (balance unchanged).</p>
              {(ledgerNewestFirst ? [...treasury.ledger].reverse() : treasury.ledger).slice(Math.min(ledgerPage, Math.max(0, Math.ceil(treasury.ledger.length / ledgerPageSize) - 1)) * ledgerPageSize, (Math.min(ledgerPage, Math.max(0, Math.ceil(treasury.ledger.length / ledgerPageSize) - 1)) + 1) * ledgerPageSize).map((row) => (
                <div className="ledger-row" key={`${row.txHash}-${row.blockNumber}-${row.eventName}-${row.runningBalanceAtomic}`}>
                  <span data-label="When">{row.blockTimestamp ? formatDate(row.blockTimestamp) : `block ${row.blockNumber.toLocaleString()}`}</span>
                  <span data-label="Event">{row.eventName === 'InboundRecorded' ? 'Entry received' : row.eventName === 'ExternalSettlement' ? 'Paid out' : row.eventName === 'MemberWithdrawal' ? 'Member withdrawal' : row.eventName === 'LiquidationPaid' ? 'Liquidation paid' : row.eventName === 'OwnerWithdrawal' ? 'Owner withdrawal (2-of-3)' : 'Internal allocation'}{row.eventName === 'BucketAllocated' ? <em className="treasury-tag"> {row.fromBucket ?? '?'} → {row.bucket ?? '?'}</em> : row.bucket ? <em className="treasury-tag"> {row.bucket}</em> : null}</span>
                  <span data-label="From">{row.memberBdId ? <code>{row.memberBdId}</code> : row.refId ? <code>{row.refId}</code> : '—'} <a href={`${EXPLORER_URL}/tx/${row.txHash}`} target="_blank" rel="noreferrer" title={row.txHash}><ExternalLink size={11} /></a></span>
                  <strong data-label="Amount" className={row.deltaAtomic.startsWith('-') ? 'negative' : ''}>{row.eventName === 'BucketAllocated' ? '↔ ' : row.deltaAtomic.startsWith('-') ? '−' : '+'}{formatToken(atomicToDecimal(row.amountAtomic))}{row.eventName === 'BucketAllocated' ? ' (moved, no change)' : ''}</strong>
                  <strong data-label="Balance after">{formatToken(atomicToDecimal(row.runningBalanceAtomic))}</strong>
                </div>
              ))}
              {treasury.ledger.length === 0 ? <p className="empty-copy">No finalized treasury events are indexed yet.</p> : null}
            </div>
            <div className="route-total"><CheckCircle2 size={15} /><span>Indexed net balance</span><strong>{formatToken(atomicToDecimal(treasury.indexed.netAtomic))}</strong></div>
            {treasury.buckets.length > 0 ? (
              <>
                <div className="panel-heading admin-subhead"><div><span className="eyebrow">ON-CHAIN SUB-ACCOUNTS</span><h3>Internal allocations by bucket</h3></div></div>
                <div className="ledger-head"><span>Bucket</span><span>Allocations</span><span>Total</span><span /><span /></div>
                {treasury.buckets.map((bucket) => (
                  <div className="ledger-row" key={bucket.bucket}><code data-label="Bucket">{bucket.bucket}</code><span data-label="Allocations">{bucket.count.toLocaleString()}</span><strong data-label="Total">{formatToken(atomicToDecimal(bucket.amountAtomic))}</strong><span /><span /></div>
                ))}
              </>
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="admin-grid" id="admin-rpc">
        <article className="panel admin-rpc">
          <div className="panel-heading"><div><span className="eyebrow">RPC MANAGER</span><h2>Provider health</h2></div><button type="button" className="button ghost small" disabled={Boolean(busy) || !wallet.csrfToken} onClick={() => void perform('probe', '/rpc/probe')}><RefreshCw size={14} /> {busy === 'probe' ? 'Probing...' : 'Probe now'}</button></div>
          {rpc.map((provider) => <div className="admin-provider" key={provider.id}><span className={`provider-dot ${provider.healthy ? 'healthy' : 'degraded'}`} /><div><strong>{provider.id}</strong><small>Priority {provider.priority} · failures {provider.consecutiveFailures}</small></div><div className="latency-meter"><i style={{ width: `${Math.min(100, (provider.latencyMs ?? 0) / 7)}%` }} /></div><strong>{provider.latencyMs === null ? 'n/a' : `${provider.latencyMs} ms`}</strong><StatusPill tone={provider.healthy ? 'green' : 'amber'}>{provider.healthy ? 'healthy' : 'degraded'}</StatusPill></div>)}
          {rpc.length === 0 ? <p className="empty-copy">No RPC health rows are available.</p> : null}
        </article>

        <article className="panel admin-indexer">
          <div className="panel-heading"><div><span className="eyebrow">CHAIN INDEXER</span><h2>Indexer</h2></div><StatusPill tone={data.indexer.state === 'error' ? 'amber' : data.indexer.enabled ? 'green' : 'neutral'}><CheckCircle2 size={11} /> {data.indexer.enabled ? (data.indexer.state ?? 'idle') : 'disabled'}</StatusPill></div>
          <div className="indexer-block"><Blocks /><div><span>Finalized checkpoint</span><strong>{data.indexer.lastFinalizedBlock?.toLocaleString() ?? 'Not available'}</strong><small>{data.indexer.updatedAt ? formatDate(data.indexer.updatedAt) : 'No checkpoint timestamp'}</small></div></div>
          <dl><div><dt>Scanned block</dt><dd>{data.indexer.lastScannedBlock?.toLocaleString() ?? 'n/a'}</dd></div><div><dt>Run count</dt><dd>{data.indexer.runCount?.toLocaleString() ?? 'n/a'}</dd></div><div><dt>Last error</dt><dd>{data.indexer.lastError ?? 'None reported'}</dd></div><div><dt>Database mode</dt><dd>WAL · foreign keys</dd></div></dl>
          <div className="join-actions"><button className="button secondary" type="button" disabled={Boolean(busy) || !data.indexer.enabled || !wallet.csrfToken} onClick={() => void perform('run', '/indexer/run')}><RefreshCw size={15} /> {busy === 'run' ? 'Running...' : 'Run once'}</button><button className="button secondary" type="button" disabled={Boolean(busy) || !data.indexer.enabled || !wallet.csrfToken} onClick={() => void perform('reconcile', '/indexer/reconcile')}><Database size={15} /> {busy === 'reconcile' ? 'Checking...' : 'Reconcile'}</button><button className="button danger" type="button" disabled={Boolean(busy) || !data.indexer.enabled || !wallet.csrfToken} onClick={() => void perform('rebuild', '/indexer/rebuild')}><Database size={15} /> {busy === 'rebuild' ? 'Rebuilding...' : 'Rebuild'}</button></div>
        </article>
      </section>

      <section className="admin-grid lower-admin">
        <article className="panel governance-queue"><div className="panel-heading"><div><span className="eyebrow">GOVERNANCE</span><h2>Proposal queue</h2></div><StatusPill tone="neutral">Not configured</StatusPill></div><p className="empty-copy">No proposal storage or timelock adapter is configured in this build. The UI does not invent pending governance actions.</p><div className="governance-note"><ShieldCheck /><span>Future controls must require compatible contracts, multisig execution, timelock expiry, and a public event.</span></div></article>
        <article className="panel emergency-controls"><div className="panel-heading"><div><span className="eyebrow">LIMITED SCOPE</span><h2>Emergency controls</h2></div><StatusPill tone="amber">Policy-gated</StatusPill></div><div className="control-row"><span><PauseCircle /></span><div><strong>Pause new joins</strong><small>No verified governance adapter is configured</small></div><button className="button danger small" type="button" disabled>Unavailable</button></div><div className="control-row"><span><Activity /></span><div><strong>Pause redemptions</strong><small>No verified governance adapter is configured</small></div><button className="button danger small" type="button" disabled>Unavailable</button></div><p><KeyRound /> Controls remain disabled until their exact on-chain authorization path is deployed and audited.</p></article>
      </section>

      {/* Owner 2026-10-11: the off-chain redemption queue (a legacy demo-only table, empty on the real system) is no longer
          shown - Primary Token redemptions / liquidations are the contracts' own events, shown in the token reports above. */}
      <section className="panel audit-table" id="admin-audit"><div className="panel-heading"><div><span className="eyebrow">SECURITY JOURNAL</span><h2>Recent admin audit</h2></div><button className="button ghost small" type="button" disabled={audit.length === 0} onClick={exportAudit}><ScrollText size={14} /> Export records</button></div>
        <div className="ledger-head"><span>Time</span><span>Actor</span><span>Action</span><span>Target</span><span>Result</span></div>
        {audit.map((entry) => <div className="ledger-row" key={entry.id}><span>{formatDate(entry.createdAt)}</span><code>{entry.actorWallet ?? 'anonymous'}</code><span>{entry.action}</span><span>{entry.resourceId ? `${entry.resource}:${entry.resourceId}` : entry.resource}</span><StatusPill tone={entry.outcome === 'success' ? 'green' : 'amber'}>{entry.outcome}</StatusPill></div>)}
        {audit.length === 0 ? <p className="empty-copy">No audit records are available in this session.</p> : null}
      </section>
      <div className="admin-footnote"><Gauge /><span>Read data refreshes every 15 seconds. Mutations resolve current authorization server-side.</span><BarChart3 /></div>
    </div>
  );
}
